<template>
  <div class="explore-page">

    <div v-if="farmMode" class="farm-travel-mode">
      <div class="farm-travel-toolbar">
        <div>
          <h3>真灵游历</h3>
          <p>真灵小队，先组队，再选择地图出发</p>
        </div>
        <div class="farm-travel-toolbar-actions">
          <div class="farm-travel-team-quick">
            <div class="farm-travel-team-quick-head"><span>真灵小队</span><small>{{ teamList.length }} 支 · {{ exploreRecords.filter(record => Number(record.status) === 1).length }} 支进行中</small><Button size="small" type="primary" @click="createTeam">创建队伍</Button><Button size="small" :disabled="!selectedTeam || Number(selectedTeam.status) !== 0" @click="selectedTeam && Number(selectedTeam.status) === 0 && editTeam(selectedTeam)">编辑队伍</Button></div>
            <div class="farm-travel-team-quick-list"><button v-for="team in teamList" :key="'quick-team-' + team.teamId" type="button" :class="{ active: String(selectedTeamId) === String(team.teamId) }" @click="onTeamChange(team.teamId)">{{ team.teamName }}<small>{{ formatCombatPower(calculateTeamPower(team)) }} · {{ getTeamStatusText(team.status) }}</small></button><span v-if="!teamList.length">暂无小队，请先创建队伍</span></div>
          </div>
          <Button size="small" icon="md-refresh" :loading="refreshing" @click="refreshData">刷新状态</Button>
        </div>
      </div>
      <div class="farm-travel-summary">
        <div><small>当前小队</small><strong>{{ selectedTeam ? selectedTeam.teamName : '未选择' }}</strong><span>{{ selectedTeam ? formatCombatPower(calculateTeamPower(selectedTeam)) + ' 战力' : '请先选择小队' }}</span></div>
        <div><small>目标地图</small><strong>{{ selectedMap ? selectedMap.mapName : '未选择' }}</strong><span>{{ selectedMap ? getMapTypeText(selectedMap.mapType) : '点击地图选择' }}</span></div>
        <div><small>派遣方案</small><strong>{{ selectedStrategyName }} · {{ exploreHours }}小时</strong><span>预计消耗体力 {{ requiredStarvationCost }}</span></div>
        <div :class="{ 'summary-ready': canStartExplore, 'summary-warning': !canStartExplore }"><small>出发状态</small><strong>{{ canStartExplore ? '可以出发' : '需要调整' }}</strong><span>{{ launchBlockingReason || '当前配置满足出发条件' }}</span></div>
      </div>
      <div class="farm-travel-layout">
        <div class="farm-travel-map" :style="{ backgroundImage: 'url(' + worldMap + ')' }">
          <img class="farm-travel-map-image" :src="worldMap" alt="" aria-hidden="true" />
          <div class="farm-travel-map-shade"></div>
          <div class="farm-travel-map-shortcuts">
            <div class="farm-travel-map-shortcuts-head"><strong>快捷出发</strong><span>点击标签自动套用地图、策略和时长</span></div>
            <div class="farm-travel-map-shortcuts-list">
              <button v-for="plan in exploreRecommendations" :key="'map-plan-' + plan.key" type="button" class="farm-travel-shortcut" :class="{ active: isRecommendationActive(plan), disabled: !findMapByRecommendation(plan) }" :disabled="!findMapByRecommendation(plan)" @click="applyExploreRecommendation(plan)">{{ plan.title }}<small>{{ plan.mapName }} · {{ getStrategyLabel(plan.strategy) }} · {{ plan.hours }}小时</small></button>
              <button v-for="material in awakeningMaterialGuides" :key="'map-material-' + material.itemId" type="button" class="farm-travel-shortcut farm-travel-shortcut--material" :class="{ active: isRecommendationActive(material) }" @click="applyAwakeningMaterialGuide(material)">{{ material.itemName }}<small>{{ material.mapName }} · {{ material.hours }}小时</small></button>
            </div>
          </div>
          <svg class="farm-travel-roads" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><polyline v-for="road in farmTravelRoads" :key="road.id" :points="road.points" /></svg>
          <button type="button" class="farm-travel-origin" :class="{ active: !selectedMapId }" aria-label="洞府" @click="clearFarmTravelSelection"><i>府</i><span>洞府</span></button>
          <button v-for="(map, index) in farmTravelNodes" :key="'farm-map-' + map.mapId" type="button" :class="['farm-travel-marker', 'map-type-' + map.mapType, { active: String(selectedMapId) === String(map.mapId), running: farmTravelRecordsForMap(map).length }]" :style="farmTravelMapStyle(map, index)" @click="selectMap(map)">
            <i><b v-if="farmTravelRecordsForMap(map).length">{{ farmTravelRecordsForMap(map).length }}</b></i><span>{{ map.mapName }}</span><small v-if="farmTravelRecordsForMap(map).length">{{ farmTravelRecordsForMap(map).length }} 支队伍在此</small>
          </button>
          <button v-for="(record, index) in farmTravelRecords" :key="'farm-team-' + record.recordId" type="button" class="farm-travel-team-marker" :class="{ active: selectedFarmTravelRecordId === record.recordId, completed: Number(record.status) === 2 }" :style="farmTravelTeamMapStyle(record, index)" @click.stop="selectFarmTravelRecord(record)">
            <i><b>{{ Number(record.status) === 2 ? '兑' : '行' }}</b></i><span>{{ getRecordTeamName(record) }}</span><small>{{ Number(record.status) === 2 ? '可领取' : '游历中' }}</small>
          </button>
          <div v-if="!mapList.length" class="farm-travel-map-empty">暂无可用游历地图</div>
        </div>
        <aside class="farm-travel-detail">
          <template v-if="selectedMap">
            <div class="farm-travel-detail-head"><div><span>{{ getMapTypeText(selectedMap.mapType) }}</span><h4>{{ selectedMap.mapName }}</h4></div><strong>{{ selectedMap.minXiao || 1 }}小时起</strong></div>
            <p class="farm-travel-description">{{ selectedMap.describeTion || '此地灵机流转，适合派遣真灵游历。' }}</p>
            <div class="farm-travel-detail-stats"><span><small>推荐战力</small><b>{{ formatCombatPower(selectedMap.minCb) }} - {{ formatCombatPower(selectedMap.maxCb) }}</b></span><span><small>体力消耗</small><b>{{ requiredStarvationCost }}</b></span></div>
            <div class="farm-travel-detail-fit"><span>当前匹配</span><strong :class="canStartExplore ? 'fit-ok' : 'fit-warning'">{{ powerMatchText }}</strong><small>{{ selectedTeam ? '小队已选，可继续确认策略与时长' : '请选择一支真灵小队' }}</small></div>
            <div v-if="farmTravelRecordsForMap(selectedMap).length && !selectedFarmTravelRecord" class="farm-travel-active-list">
              <div class="farm-travel-active-list-head"><strong>此地游历小队</strong><span>{{ farmTravelRecordsForMap(selectedMap).length }} 支</span></div>
              <button v-for="record in farmTravelRecordsForMap(selectedMap)" :key="record.recordId" type="button" :class="['farm-travel-active-row', { active: selectedFarmTravelRecordId === record.recordId }]" @click="selectFarmTravelRecord(record)"><span><b>{{ getRecordTeamName(record) }}</b><small>{{ getRecordHint(record) }}</small></span><em>{{ Number(record.status) === 2 ? '确认' : '查看' }}</em></button>
            </div>
            <div v-if="selectedFarmTravelRecord && String(selectedFarmTravelRecord.mapId) === String(selectedMap.mapId)" class="farm-travel-running">
              <div><span>当前小队</span><strong>{{ getRecordTeamName(selectedFarmTravelRecord) }}</strong></div>
              <small>{{ getRecordHint(selectedFarmTravelRecord) }}</small>
              <Button v-if="Number(selectedFarmTravelRecord.status) === 2" type="primary" size="small" :loading="completingRecordId === selectedFarmTravelRecord.recordId" @click="completeExplore(selectedFarmTravelRecord.recordId)">收获游历成果</Button>
            </div>
            <div v-if="!selectedFarmTravelRecord || String(selectedFarmTravelRecord.mapId) !== String(selectedMap.mapId)" class="farm-travel-config">
              <!-- <label>出发小队</label><Select v-model="selectedTeamId" size="small" @on-change="onTeamChange"><Option v-for="team in teamList" :key="team.teamId" :value="team.teamId">{{ team.teamName }} · {{ formatCombatPower(calculateTeamPower(team)) }}</Option></Select> -->
              <div class="farm-travel-inline-setting"><label>策略</label><div class="farm-travel-choice-list"><button v-for="strategy in strategyOptions" :key="strategy.value" type="button" :title="strategy.desc" :class="{ active: selectedStrategy === strategy.value }" @click="selectedStrategy = strategy.value">{{ strategy.label }}</button></div></div>
              <div class="farm-travel-inline-setting"><label>时长</label><div class="farm-travel-hours"><button v-for="hour in hourOptions" :key="hour" type="button" :class="{ active: exploreHours === hour }" @click="setExploreHours(hour)">{{ hour }}小时</button></div></div>
              <div class="farm-travel-finish-time"><span>预计完成</span><strong>{{ selectedMap && selectedTeam ? exploreFinishTime : '选择地图和小队后显示' }}</strong></div>
              <!-- <div class="farm-travel-match"><span>{{ powerMatchText }}</span><small>{{ launchBlockingReason || '当前配置满足出发条件。' }}</small></div> -->
              <Button class="farm-travel-start-button" type="primary" :disabled="!canStartExplore" :loading="exploreLoading" @click="startExplore">开始游历</Button>
            </div>
          </template>
          <div v-else class="farm-travel-map-empty">点击地图上的地点查看详情</div>
        </aside>
      </div>
      <div class="farm-travel-bottom"><div class="farm-travel-record-strip"><strong>最近游历</strong><button v-for="record in exploreRecords.slice(0, 4)" :key="record.recordId" type="button" @click="selectFarmTravelRecord(record)"><span>{{ getRecordTeamName(record) }}</span><small>{{ getRecordMapName(record) }} · {{ getRecordStatusText(record.status) }}</small></button><span v-if="!exploreRecords.length">暂无游历记录</span></div></div>
    </div>

    <div v-else class="main-layout">
      <div class="side-nav">
        <div :class="['nav-item', { active: activeTab === 'guide' }]" @click="activeTab = 'guide'">
          <Icon type="md-book" size="18" />
          <span>游历说明</span>
        </div>
        <div :class="['nav-item', { active: activeTab === 'map' }]" @click="activeTab = 'map'">
          <Icon type="md-map" size="18" />
          <span>地图选择</span>
          <Tag v-if="selectedMap" size="small" color="green">{{ selectedMap.mapName }}</Tag>
        </div>
        <div :class="['nav-item', { active: activeTab === 'team' }]" @click="activeTab = 'team'">
          <Icon type="md-send" size="18" />
          <span>队伍出发</span>
          <Tag v-if="selectedTeam" size="small" :color="getTeamStatusColor(selectedTeam.status)">
            {{ getTeamStatusText(selectedTeam.status) }}
          </Tag>
        </div>
      </div>

      <div class="content-area">
        <!-- 游历说明 Tab -->
        <div v-show="activeTab === 'guide'" class="tab-content">
          <Card class="panel-card" :bordered="false">
            <div slot="title" class="panel-title">
              <span>游历说明</span>
              <span class="panel-subtitle">了解真灵游历的核心机制</span>
            </div>
            <div class="guide-content">
              <div class="guide-section">
                <div class="guide-section-title">队伍编组</div>
                <p>组好队伍后，1号位偏稳定保底，2号位偏探索收获，3号位偏辅助奇遇。</p>
                <div class="guide-grid">
                  <div class="guide-card">
                    <div class="guide-card-num">1</div>
                    <div><strong>领队位</strong></div>
                    <p>偏稳定、抗风险、保底表现，适合放更稳、更肉、综合能力高的真灵。领队位更像队伍的主心骨，决定这次游历稳不稳。</p>
                  </div>
                  <div class="guide-card">
                    <div class="guide-card-num">2</div>
                    <div><strong>探索位</strong></div>
                    <p>偏输出、探索和主要收获，通常放队伍里最强、最适合当前地图的真灵。探索位往往决定你这趟能不能拿到更好的材料。</p>
                  </div>
                  <div class="guide-card">
                    <div class="guide-card-num" style="background: #e8b84b;">3</div>
                    <div><strong>辅助位</strong></div>
                    <p>偏辅助、补强和奇遇，适合功能型、资源型、灵活型真灵。辅助位不一定战力最高，但很适合补收益或补短板。</p>
                  </div>
                </div>
                <div class="guide-tip">
                  <Icon type="ios-bulb" color="#e8b84b" size="16" />
                  <span>不会摆位时：最稳的放 1 号位，最强的放 2 号位，功能型放 3 号位。</span>
                </div>
              </div>

              <div class="guide-section">
                <div class="guide-section-title">地图与策略</div>
                <div class="guide-grid guide-grid-2">
                  <div class="guide-card">
                    <div><strong>地图选择</strong></div>
                    <p>优先看推荐战力、每小时体力消耗和掉落倾向。低阶地图更适合刷基础材料，中高阶地图更适合刷觉醒材料、血脉材料和后期成长材料。</p>
                  </div>
                  <div class="guide-card">
                    <div><strong>策略选择</strong></div>
                    <p>均衡最稳，适合大多数情况；稳健适合日常刷材料；冒险和深探更看脸，但更容易遇到高价值事件；疾行适合赶时间、追效率。</p>
                  </div>
                </div>
                <div class="guide-tip">
                  <Icon type="ios-bulb" color="#e8b84b" size="16" />
                  <span>战力高不一定收益最高，搭配合理更重要。游历会综合看位置搭配、真灵类型、地图适配、策略和随机事件。</span>
                </div>
              </div>

              <div class="guide-section">
                <div class="guide-section-title">材料与用途</div>
                <div class="guide-grid guide-grid-2">
                  <div class="guide-card">
                    <div><strong>游历产出</strong></div>
                    <p>主要产出觉醒材料、血脉材料、真灵包裹材料和后期属性提升材料。觉醒材料决定能不能升阶，属性材料决定升阶后强不强。</p>
                  </div>
                  <div class="guide-card">
                    <div><strong>真灵包裹</strong></div>
                    <p>奖励会进入真灵包裹，不同真灵拿到的物品会分开存放。在真灵详情或游历页面里查看，并把需要的材料取回角色背包。</p>
                  </div>
                </div>
                <div class="guide-grid guide-grid-2" style="margin-top: 12px;">
                  <div class="guide-card">
                    <div><strong>真灵觉醒</strong></div>
                    <p>真灵达到等级上限后才可以觉醒。觉醒主要消耗游历掉落的材料和灵石，游历是日常必须做的内容之一。</p>
                  </div>
                  <div class="guide-card">
                    <div><strong>新手建议</strong></div>
                    <p>前期先用稳健或均衡策略刷基础材料；缺觉醒材料时再去对应地图；队伍强了再用冒险或深探去冲高价值掉落和稀有事件。</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <!-- 地图选择 Tab -->
        <div v-show="activeTab === 'map'" class="tab-content">
          <Card class="panel-card" :bordered="false">
            <div slot="title" class="panel-title">
              <span>选择游历地图</span>
              <span class="panel-subtitle">共 {{ mapList.length }} 张地图</span>
            </div>
            <div class="map-grid">
              <div
                v-for="map in mapList"
                :key="map.mapId"
                :class="['map-block', { active: selectedMapId === map.mapId }]"
                @click="selectMap(map)"
              >
                <div class="map-block-head">
                  <div class="map-block-name">{{ map.mapName }}</div>
                  <Tag :color="selectedMapId === map.mapId ? 'gold' : 'default'" size="small">
                    {{ map.minXiao || 1 }}/h 起
                  </Tag>
                </div>
                <div class="map-block-type">{{ getMapTypeText(map.mapType) }}</div>
                <p class="map-block-desc" :title="map.describeTion || '此地灵机流转，适合派遣真灵游历。'">{{ map.describeTion || '此地灵机流转，适合派遣真灵游历。' }}</p>
                <div class="map-block-foot">
                  <span>推荐战力</span>
                  <strong>{{ formatCombatPower(map.minCb) }} - {{ formatCombatPower(map.maxCb) }}</strong>
                </div>
                <div v-if="selectedMapId === map.mapId" class="map-block-check">
                  <Icon type="md-checkmark-circle" color="#2d8cf0" size="20" />
                </div>
              </div>
              <Empty v-if="!mapList.length" description="暂无地图" />
            </div>
            <Alert v-if="selectedMap" type="info" show-icon class="panel-alert">
              <div>最低探索时长：{{ selectedMap.minXiao || 1 }} 小时</div>
              <div>推荐战力区间：{{ formatCombatPower(selectedMap.minCb) }} - {{ formatCombatPower(selectedMap.maxCb) }}</div>
              <div v-if="selectedTeam">当前小队匹配：{{ powerMatchText }}</div>
            </Alert>
          </Card>

          <Card class="panel-card" :bordered="false" style="margin-top: 8px;">
            <div slot="title" class="panel-title">
              <span>游历策略</span>
              <span class="panel-subtitle">影响事件倾向与收益</span>
            </div>
            <RadioGroup v-model="selectedStrategy" class="strategy-group">
              <div
                v-for="strategy in strategyOptions"
                :key="strategy.value"
                :class="['strategy-card', { active: selectedStrategy === strategy.value }]"
                @click="selectedStrategy = strategy.value"
              >
                <div class="strategy-card-head">
                  <Radio :label="strategy.value">
                    <span class="strategy-name">{{ strategy.label }}</span>
                  </Radio>
                  <Tag v-if="selectedStrategy === strategy.value" color="blue" size="small">当前</Tag>
                </div>
                <p class="strategy-desc">{{ strategy.desc }}</p>
              </div>
            </RadioGroup>
          </Card>

          <Card class="panel-card" :bordered="false" style="margin-top: 8px;">
            <div slot="title" class="panel-title">
              <span>游历时长</span>
              <span class="panel-subtitle">时长越长，过程事件越多</span>
            </div>
            <div class="hours-block">
              <div class="hours-list">
                <Button
                  v-for="hour in hourOptions"
                  :key="hour"
                  :type="exploreHours === hour ? 'primary' : 'default'"
                  size="large"
                  @click="setExploreHours(hour)"
                >
                  {{ hour }} 小时
                </Button>
              </div>
              <div class="hours-hint">
                当前选择 <strong>{{ exploreHours }} 小时</strong>，预计消耗体力约 {{ exploreHours * (selectedMap ? (selectedMap.minXiao || 1) : 1) }}
              </div>
            </div>
          </Card>

          <Card class="panel-card" :bordered="false" style="margin-top: 8px;">
            <div slot="title" class="panel-title">
              <span>推荐搭配</span>
              <span class="panel-subtitle">点击后自动套用地图、策略和时长</span>
            </div>
            <div class="recommend-grid">
              <div
                v-for="plan in exploreRecommendations"
                :key="plan.key"
                :class="['recommend-card', { active: isRecommendationActive(plan), disabled: !findMapByRecommendation(plan) }]"
                @click="applyExploreRecommendation(plan)"
              >
                <div class="recommend-head">
                  <strong>{{ plan.title }}</strong>
                  <Tag :color="plan.color" size="small">{{ plan.hours }}小时 · {{ getStrategyLabel(plan.strategy) }}</Tag>
                </div>
                <div class="recommend-route">{{ plan.mapName }} / {{ plan.modeText }}</div>
                <p>{{ plan.desc }}</p>
                <div class="recommend-yield">{{ plan.yieldText }}</div>
              </div>
            </div>

            <div class="material-guide">
              <div class="material-guide-title">觉醒材料定向</div>
              <div class="material-guide-grid">
                <div
                  v-for="material in awakeningMaterialGuides"
                  :key="material.itemId"
                  class="material-guide-row"
                  @click="applyAwakeningMaterialGuide(material)"
                >
                  <div class="material-guide-main">
                    <strong>{{ material.itemName }}</strong>
                    <span>{{ material.mapName }}</span>
                  </div>
                  <Tag :color="material.color" size="small">{{ material.hours }}小时 · {{ getStrategyLabel(material.strategy) }}</Tag>
                </div>
              </div>
            </div>

            <Alert type="warning" show-icon class="panel-alert">
              推荐只提高对应事件和材料池命中倾向，不代表必出；战力不足时先退回低阶图或改稳健模式。
            </Alert>
          </Card>
        </div>

        <!-- 队伍出发 Tab -->
        <div v-show="activeTab === 'team'" class="tab-content">
          <!-- 摘要条 -->
          <div class="launch-summary-bar">
            <div class="summary-item" @click="activeTab = 'map'">
              <span class="summary-label">地图</span>
              <strong>{{ selectedMap ? selectedMap.mapName : '未选择' }}</strong>
              <Icon type="ios-arrow-forward" size="14" />
            </div>
            <div class="summary-divider" />
            <div class="summary-item" @click="activeTab = 'map'">
              <span class="summary-label">策略</span>
              <strong>{{ selectedStrategyName }}</strong>
              <Icon type="ios-arrow-forward" size="14" />
            </div>
            <div class="summary-divider" />
            <div class="summary-item" @click="activeTab = 'map'">
              <span class="summary-label">时长</span>
              <strong>{{ exploreHours }} 小时</strong>
              <Icon type="ios-arrow-forward" size="14" />
            </div>
          </div>

          <!-- 队伍配置 / 游历记录 / 过程战报 -->
          <Card class="panel-card" :bordered="false">
            <div slot="title" class="panel-title">
              <div class="record-tabs">
                <div :class="['record-tab', { active: recordTab === 'team' }]" @click="recordTab = 'team'">
                  队伍配置
                </div>
                <div :class="['record-tab', { active: recordTab === 'records' }]" @click="recordTab = 'records'">
                  游历记录
                  <Tag color="cyan" size="small">{{ exploreRecords.length }}</Tag>
                </div>
                <div :class="['record-tab', { active: recordTab === 'battle' }]" @click="recordTab = 'battle'">
                  过程战报
                  <Tag v-if="activeRecord && recordTab === 'battle'" color="purple" size="small">{{ getRecordMapName(activeRecord) }}</Tag>
                </div>
              </div>
              <Button v-if="recordTab === 'records'" size="small" @click="loadExploreRecords" icon="md-refresh">刷新记录</Button>
            </div>

            <div class="record-scroll-area">
              <!-- 队伍配置 -->
              <div v-show="recordTab === 'team'">
                <div v-if="teamList.length" class="team-selector-row">
                  <Select v-model="selectedTeamId" @on-change="onTeamChange" size="small" style="width: 220px;">
                    <Option v-for="team in teamList" :key="team.teamId" :value="team.teamId">
                      {{ team.teamName }}
                    </Option>
                  </Select>
                  <Button size="small" @click="manageTeam" :disabled="!selectedTeam || selectedTeam.status !== 0">
                    编辑小队
                  </Button>
                  <Button size="small" @click="editingTeam = null; teamForm = { teamName: '', selectedAuras: [] }; showCreateTeam = true">
                    创建小队
                  </Button>
                  <Button size="small" icon="md-refresh" :loading="refreshing" @click="refreshData">刷新</Button>
                </div>
                <div v-if="selectedTeam" class="team-panel">
                  <div class="team-summary">
                    <div>
                      <div class="team-name">{{ selectedTeam.teamName }}</div>
                      <div class="team-subline">
                        总战力 {{ formatCombatPower(calculateTeamPower(selectedTeam)) }}
                        <span class="dot">·</span>
                        平均等级 Lv.{{ calculateTeamLevel(selectedTeam) }}
                      </div>
                    </div>
                    <Tag :color="getTeamStatusColor(selectedTeam.status)">
                      {{ getTeamStatusText(selectedTeam.status) }}
                    </Tag>
                  </div>

                  <div class="slot-row">
                    <div v-for="slot in roleSlots" :key="slot.slot" class="slot-block">
                      <div class="slot-block-head">
                        <span class="slot-index">{{ slot.slot }}号位</span>
                        <Tag :color="slot.color" size="small">{{ slot.effect }}</Tag>
                      </div>
                      <div class="slot-block-role">{{ slot.label }}</div>
                      <div v-if="getTeamAuraBySlot(selectedTeam, slot.slot)" class="slot-block-body">
                        <img
                          :src="getTeamAuraBySlot(selectedTeam, slot.slot).auraIcon || defaultAvatar"
                          class="slot-block-avatar"
                        />
                        <div class="slot-block-name">{{ getTeamAuraBySlot(selectedTeam, slot.slot).auraName }}</div>
                        <div class="slot-block-stats">
                          Lv.{{ getTeamAuraBySlot(selectedTeam, slot.slot).auraLevel || 1 }}
                          <span class="dot">·</span>
                          {{ formatCombatPower(getTeamAuraBySlot(selectedTeam, slot.slot).auraCombot) }}
                        </div>
                      </div>
                      <div v-else class="slot-block-empty">未配置真灵</div>
                    </div>
                  </div>

                  <div class="launch-section">
                    <div class="launch-section-title">出发确认</div>
                    <div class="launch-confirm-layout">
                      <div class="launch-check">
                        <div class="check-row">
                          <span>战力匹配</span>
                          <strong :class="canStartExplore ? 'ok-text' : 'warn-text'">{{ powerMatchText }}</strong>
                        </div>
                        <div class="check-row">
                          <span>小队战力</span>
                          <strong>{{ formatCombatPower(calculateTeamPower(selectedTeam)) }}</strong>
                        </div>
                        <div class="check-row">
                          <span>推荐战力</span>
                          <strong>{{ selectedMap ? formatCombatPower(selectedMap.minCb) + ' - ' + formatCombatPower(selectedMap.maxCb) : '-' }}</strong>
                        </div>
                      </div>
                      <div class="launch-alerts">
                        <Alert v-if="selectedTeam.status === 1" type="warning" show-icon>
                          小队正在游历中，需等待结束后再次派遣。
                        </Alert>
                        <Alert v-else-if="selectedTeam.status === 2" type="warning" show-icon>
                          小队处于休整中，结束时间：{{ formatTime(selectedTeam.restEndTime) }}。
                        </Alert>
                        <Alert v-for="issue in selectedTeamIssues" :key="issue.text" :type="issue.type" show-icon>
                          {{ issue.text }}
                        </Alert>
                        <Alert v-if="launchBlockingReason" type="warning" show-icon>
                          {{ launchBlockingReason }}
                        </Alert>
                        <Alert v-else-if="selectedMap" type="success" show-icon>
                          当前条件满足，可以开始游历。
                        </Alert>
                      </div>
                      <Button
                        type="primary"
                        size="large"
                        icon="ios-rocket"
                        :disabled="!canStartExplore"
                        :loading="exploreLoading"
                        @click="startExplore"
                        class="launch-btn"
                      >
                        开始游历
                      </Button>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-action">
                  <p>暂无小队，先创建一个游历小队。</p>
                  <Button type="primary" @click="showCreateTeam = true">创建小队</Button>
                </div>
              </div>

              <!-- 游历记录 -->
              <div v-show="recordTab === 'records'">
                <div v-if="exploreRecords.length" class="record-list">
                  <div
                    v-for="record in exploreRecords"
                    :key="record.recordId"
                    :class="['record-card', { active: activeRecordId === record.recordId }]"
                    @click="selectRecord(record); recordTab = 'battle'"
                  >
                    <div class="record-head">
                      <Tag :color="getRecordStatusColor(record.status)">
                        {{ getRecordStatusText(record.status) }}
                      </Tag>
                      <span class="record-time">{{ formatTime(record.startTime || record.createTime) }}</span>
                    </div>
                    <div class="record-title">
                      {{ getRecordTeamName(record) }} · {{ getRecordMapName(record) }}
                    </div>
                    <p class="record-desc" :title="record.processDesc || ''">{{ record.processDesc || '本次游历暂无摘要。' }}</p>
                    <div class="record-note">{{ getRecordHint(record) }}</div>
                    <div v-if="getMapExploreProgress(record)" class="record-note">
                      地图探索度：{{ getMapExploreProgress(record) }}
                      <span v-if="getMapExploreGain(record)">（本次 +{{ getMapExploreGain(record) }}）</span>
                    </div>
                    <div class="record-foot">
                      <span>时长 {{ record.exploreHours || 0 }} 小时</span>
                      <div class="record-actions">
                        <Button size="small" @click.stop="viewProcess(record); recordTab = 'battle'">
                          查看过程
                        </Button>
                        <Button
                          v-if="record.status === 2"
                          size="small"
                          type="primary"
                          :loading="completingRecordId === record.recordId"
                          @click.stop="completeExplore(record.recordId)"
                        >
                          领取奖励
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <Empty v-else description="暂无游历记录" />
              </div>

              <!-- 过程战报 -->
              <div v-show="recordTab === 'battle'">
                <div v-if="activeRecord" class="process-header-card">
                  <div class="process-summary-line">
                    <div class="process-record-title">{{ getRecordTeamName(activeRecord) }}</div>
                    <div class="process-record-meta">
                      {{ formatTime(activeRecord.startTime) }} 至 {{ formatTime(activeRecord.endTime) }}
                    </div>
                  </div>
                  <p class="process-record-desc">{{ activeRecord.processDesc || '暂无摘要。' }}</p>
                  <div v-if="activeRecordStats" class="process-overview">
                    <div class="expand-grid">
                      <div class="expand-item">
                        <span>总过程数</span>
                        <strong>{{ activeRecordStats.totalProcessCount || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>奖励过程</span>
                        <strong>{{ activeRecordStats.rewardProcessCount || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>纯过程</span>
                        <strong>{{ activeRecordStats.pureProcessCount || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>总真灵经验</span>
                        <strong>{{ activeRecordStats.totalAuraExp || 0 }}</strong>
                      </div>
                    </div>
                    <div class="expand-grid" style="margin-top: 10px;">
                      <div class="expand-item">
                        <span>地图探索度</span>
                        <strong>{{ activeRecordStats.mapExploreProgress || getMapExploreProgress(activeRecord) || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>本次探索增量</span>
                        <strong>+{{ activeRecordStats.mapExploreGain || getMapExploreGain(activeRecord) || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>奖励总数</span>
                        <strong>{{ activeRecordStats.rewardItemTotalCount || 0 }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>奖励种类</span>
                        <strong>{{ activeRecordStats.rewardItemTypeCount || 0 }}</strong>
                      </div>
                    </div>
                  </div>
                  <div v-if="overallProcessSummary" class="process-overview">
                    <div class="expand-grid">
                      <div class="expand-item">
                        <span>总体策略</span>
                        <strong>{{ overallProcessSummary.strategyName || '-' }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>关键真灵</span>
                        <strong>{{ overallProcessSummary.keyAuraName || '-' }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>总体结果</span>
                        <strong>{{ getResultTypeText(overallProcessSummary.resultType) }}</strong>
                      </div>
                      <div class="expand-item">
                        <span>综合分</span>
                        <strong>{{ formatScore(overallProcessSummary.phase2Score) }}</strong>
                      </div>
                    </div>
                    <div v-if="overallScoreBreakdown.length" class="score-breakdown">
                      <div v-for="item in overallScoreBreakdown" :key="item.key" class="score-item">
                        <span>{{ item.label }}</span>
                        <strong>{{ formatScore(item.value) }}</strong>
                      </div>
                    </div>
                  </div>
                  <div v-if="headerEffectTags.length" class="effect-list">
                    <Tag v-for="item in headerEffectTags" :key="item.key" :color="item.color">{{ item.text }}</Tag>
                  </div>
                </div>

                <div v-if="currentProcess.length" class="process-list">
                  <div v-for="process in currentProcess" :key="process.processId || process.hourIndex" class="process-card">
                    <div class="process-head">
                      <div class="process-row">
                        <div class="process-main">
                          <Tag :color="getRarityColor(process.rarity)">第 {{ process.hourIndex }} 小时</Tag>
                          <span class="process-event">{{ getEventTypeText(process.eventType) }}</span>
                        </div>
                        <span class="process-result-tag">{{ getResultTypeText(getVariables(process).resultType) }}</span>
                      </div>
                      <p class="process-desc-line">{{ process.description }}</p>
                    </div>
                  </div>
                </div>
                <Empty v-else :description="processEmptyText" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>

    <Modal
      v-model="showCreateTeam"
      :title="editingTeam ? '编辑真灵小队' : '创建真灵小队'"
      width="760"
      :mask-closable="false"
      class="team-modal"
    >
      <Form :model="teamForm" label-position="top">
        <FormItem label="小队名称">
          <Input v-model="teamForm.teamName" maxlength="10" show-word-limit placeholder="请输入小队名称" />
        </FormItem>

        <FormItem :label="`选择真灵（最多 ${maxTeamSize} 个）`">
          <Alert type="info" show-icon>
            当前选择 {{ teamForm.selectedAuras.length }} / {{ maxTeamSize }}
          </Alert>
          <CheckboxGroup v-model="teamForm.selectedAuras">
            <Row :gutter="14">
              <Col :xs="24" :sm="6" v-for="pet in availableAuras" :key="pet.auraLanId">
                <div
                  :class="[
                    'aura-option',
                    { selected: teamForm.selectedAuras.includes(pet.auraLanId) },
                    { disabled: isAuraDisabledInForm(pet.auraLanId) }
                  ]"
                >
                  <Checkbox :label="pet.auraLanId" :disabled="isAuraDisabledInForm(pet.auraLanId)">
                    <div class="aura-option-body">
                      <img :src="pet.auraIcon || defaultAvatar" class="aura-option-avatar" />
                      <div class="aura-option-meta">
                        <div class="aura-option-name">
                          {{ pet.auraName }}
                          <Tag v-if="isAuraInOtherTeam(pet.auraLanId)" size="small" color="red">已组队</Tag>
                          <Tag v-if="Number(pet.auraStatus || 0) === 1" size="small" color="orange">{{ teamForm.selectedAuras.includes(pet.auraLanId) ? '出战中 · 可移出' : '出战中 · 不可加入' }}</Tag>
                        </div>
                        <div class="aura-option-tags">
                          <Tag size="small" :color="getGradeColor(pet.auraGrade)">{{ getGradeName(pet.auraGrade) }}</Tag>
                          <Tag size="small" :color="getTypeColor(pet.auraType)">{{ getTypeName(pet.auraType) }}</Tag>
                        </div>
                        <div class="aura-option-stats">
                          战力 {{ formatCombatPower(pet.auraCombot) }} · Lv.{{ pet.auraLevel }} · 体力 {{ pet.auraStarvationValue }}
                        </div>
                      </div>
                    </div>
                  </Checkbox>
                </div>
              </Col>
            </Row>
          </CheckboxGroup>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="cancelCreateTeam">取消</Button>
        <Button
          v-if="editingTeam"
          type="error"
          :loading="teamLoading"
          @click="deleteTeam"
        >
          删除小队
        </Button>
        <Button
          type="primary"
          :loading="teamLoading"
          :disabled="teamForm.selectedAuras.length === 0 || hasDuplicateAuras"
          @click="saveTeam"
        >
          {{ editingTeam ? '更新小队' : '创建小队' }}
        </Button>
      </div>
    </Modal>

    <Modal v-model="rewardModalVisible" title="游历奖励" width="680">
      <div v-if="rewardResult.message" class="reward-message">{{ rewardResult.message }}</div>
      <div v-if="rewardResult.auraExpGains.length" class="reward-exp-block">
        <div class="block-title">真灵经验</div>
        <div class="reward-grid">
          <div
            v-for="gain in rewardResult.auraExpGains"
            :key="gain.auraId || gain.auraName"
            class="reward-card exp-card"
          >
            <div class="reward-name">{{ gain.auraName }}</div>
            <div class="reward-meta">
              Lv.{{ gain.levelBefore || 0 }} -> Lv.{{ gain.levelAfter || gain.levelBefore || 0 }}
            </div>
            <div class="reward-count">+{{ gain.expGain || 0 }}</div>
            <p class="reward-desc">
              <span v-if="Number(gain.levelGain || 0) > 0">本次提升 {{ gain.levelGain }} 级，</span>
              当前经验 {{ gain.currentExp || 0 }} / {{ gain.upgradeExp || 0 }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="rewardResult.rewards.length" class="reward-grid">
        <div v-for="reward in rewardResult.rewards" :key="reward.processId + reward.itemId" class="reward-card">
          <div class="reward-name">{{ reward.itemName }}</div>
          <div class="reward-meta">{{ reward.itemTypeDesc }} · 稀有度 {{ reward.rarity }}</div>
          <div class="reward-count">x{{ reward.count }}</div>
          <p class="reward-desc">{{ reward.description }}</p>
        </div>
      </div>
      <Empty v-else description="本次没有可展示的奖励明细" />
      <div v-if="rewardResult.restTime" class="reward-rest">
        休整时间：{{ rewardResult.restTime }} 分钟
      </div>
      <div slot="footer">
        <Button @click="rewardModalVisible = false">关闭</Button>
        <Button type="primary" @click="openAuraMaterialBag">查看真灵包裹</Button>
      </div>
    </Modal>

    <Modal v-model="auraMaterialBagVisible" title="真灵包裹" width="760">
      <div class="reward-message">游历材料已存入真灵包裹，物品名中会包含归属真灵。</div>
      <div class="reward-actions">
        <Button icon="ios-refresh" :loading="auraMaterialBagLoading" @click="loadAuraMaterialBag">刷新包裹</Button>
      </div>
      <div v-if="auraMaterialBagList.length" class="reward-grid">
        <div v-for="item in auraMaterialBagList" :key="item.packItemId || item.itemId || item.itemName" class="reward-card">
          <div class="reward-name">{{ item.itemName || '未知材料' }}</div>
          <div class="reward-meta">{{ item.typeDec || item.itemTypeDesc || '材料' }}</div>
          <div class="reward-count">x{{ item.itemNum || item.count || 0 }}</div>
          <p class="reward-desc">{{ item.decs || item.description || '暂无说明' }}</p>
        </div>
      </div>
      <Empty v-else :description="auraMaterialBagLoading ? '正在加载真灵包裹' : '暂无真灵包裹材料'" />
    </Modal>
  </div>
</template>

<script>
const DEFAULT_STRATEGY = 'BALANCED'
const worldMap = require('@/assets/spirit-world-ink-map.jpg')

export default {
  name: 'petMap',
  props: {
    farmMode: { type: Boolean, default: false }
  },
  data() {
    return {
      mapList: [],
      allAuras: [],
      availableAuras: [],
      teamList: [],
      exploreRecords: [],
      currentProcess: [],
      currentRecord: null,
      activeRecordId: null,
      selectedMapId: null,
      selectedMap: null,
      selectedFarmTravelRecordId: null,
      selectedTeamId: null,
      selectedTeam: null,
      selectedStrategy: localStorage.getItem('petExploreStrategy') || DEFAULT_STRATEGY,
      exploreHours: Number(localStorage.getItem('petExploreHours')) || 4,
      showCreateTeam: false,
      editingTeam: null,
      teamLoading: false,
      exploreLoading: false,
      refreshing: false,
      loadingAuras: false,
      completingRecordId: null,
      rewardModalVisible: false,
      auraMaterialBagVisible: false,
      auraMaterialBagLoading: false,
      auraMaterialBagList: [],
      rewardResult: {
        rewards: [],
        auraExpGains: [],
        restTime: 0,
        message: ''
      },
      teamForm: {
        teamName: '',
        selectedAuras: []
      },
      roleSlots: [
        { slot: 1, label: '领队位', effect: '抗伤/保底', color: 'blue' },
        { slot: 2, label: '探索位', effect: '输出/探索', color: 'green' },
        { slot: 3, label: '辅助位', effect: '辅助/奇遇', color: 'gold' }
      ],
      strategyOptions: [
        { value: 'BALANCED', label: '均衡', desc: '基础选择，整体表现更平均。' },
        { value: 'STEADY', label: '稳健', desc: '偏向平稳推进，降低波动。' },
        { value: 'ADVENTURE', label: '冒险', desc: '偏向奇遇与突破，更看脸。' },
        { value: 'RUSH', label: '疾行', desc: '偏向高效率推进，节奏更快。' },
        { value: 'DEEP', label: '深探', desc: '尝试更深入地触发高强度过程。' }
      ],
      exploreRecommendations: [
        {
          key: 'difeshan-awaken',
          title: '觉醒石优先',
          mapName: '地肺山',
          hours: 8,
          strategy: 'ADVENTURE',
          modeText: '冒险模式',
          color: 'orange',
          desc: '地肺山偏修炼、地脉、地火事件，8小时冒险更容易撞到修炼和奇遇事件。',
          yieldText: '目标：血脉觉醒石、玄脉地甲片、赤羽焰心翎。'
        },
        {
          key: 'black-island-battle',
          title: '战斗觉醒材料',
          mapName: '黑潮群岛',
          hours: 8,
          strategy: 'ADVENTURE',
          modeText: '冒险模式',
          color: 'purple',
          desc: '黑潮群岛偏战斗与高风险战利品，适合战力足够的小队刷战斗型突破材料。',
          yieldText: '目标：沧溟蛟鳞核、青玉凶魄牙、墨玉幽骨芯。'
        },
        {
          key: 'spirit-mountain-growth',
          title: '灵兽血脉材料',
          mapName: '灵兽山脉',
          hours: 8,
          strategy: 'ADVENTURE',
          modeText: '冒险模式',
          color: 'green',
          desc: '灵兽山脉偏灵兽、山脉、遭遇探索，适合养真灵和刷血脉成长材料。',
          yieldText: '目标：斗天灵纹晶、清瞳风灵羽、玄狐幻月珠、青瑶鹿灵枝。'
        },
        {
          key: 'stable-basic',
          title: '低风险补材料',
          mapName: '灵兽山脉',
          hours: 4,
          strategy: 'STEADY',
          modeText: '稳健模式',
          color: 'blue',
          desc: '小队战力刚达标时用短时稳健，先补基础成长材料和探索度。',
          yieldText: '目标：常见材料、低波动真灵包裹收益。'
        }
      ],
      awakeningMaterialGuides: [
        { itemId: '43001', itemName: '沧溟蛟鳞核', mapName: '黑潮群岛', hours: 8, strategy: 'ADVENTURE', color: 'purple' },
        { itemId: '43002', itemName: '玄脉地甲片', mapName: '地肺山', hours: 8, strategy: 'ADVENTURE', color: 'orange' },
        { itemId: '43003', itemName: '青玉凶魄牙', mapName: '黑潮群岛', hours: 8, strategy: 'ADVENTURE', color: 'purple' },
        { itemId: '43004', itemName: '斗天灵纹晶', mapName: '灵兽山脉', hours: 8, strategy: 'ADVENTURE', color: 'green' },
        { itemId: '43005', itemName: '清瞳风灵羽', mapName: '灵兽山脉', hours: 8, strategy: 'ADVENTURE', color: 'green' },
        { itemId: '43006', itemName: '赤羽焰心翎', mapName: '地肺山', hours: 8, strategy: 'ADVENTURE', color: 'orange' },
        { itemId: '43007', itemName: '玄狐幻月珠', mapName: '灵兽山脉', hours: 8, strategy: 'ADVENTURE', color: 'green' },
        { itemId: '43008', itemName: '墨玉幽骨芯', mapName: '黑潮群岛', hours: 8, strategy: 'ADVENTURE', color: 'purple' },
        { itemId: '43009', itemName: '青瑶鹿灵枝', mapName: '灵兽山脉', hours: 8, strategy: 'ADVENTURE', color: 'green' }
      ],
      hourOptions: [4, 8, 12, 24],
      maxTeamSize: 3,
      defaultAvatar: '/default-aura.png',
      activeTab: 'team',
      recordTab: 'team'
    }
  },
  computed: {
    charaId() {
      return this.getCookie('charaId')
    },
    hasDuplicateAuras() {
      return this.teamForm.selectedAuras.some(auraId => this.isAuraInOtherTeam(auraId))
    },
    selectedStrategyName() {
      const strategy = this.strategyOptions.find(item => item.value === this.selectedStrategy)
      return strategy ? strategy.label : '均衡'
    },
    requiredStarvationCost() {
      if (!this.selectedMap || !this.exploreHours) {
        return 0
      }
      return this.getMapHourlyCost(this.selectedMap) * Number(this.exploreHours || 0)
    },
    exploreFinishTime() {
      return this.formatTime(new Date(Date.now() + Number(this.exploreHours || 0) * 60 * 60 * 1000).toISOString())
    },
    canStartExplore() {
      if (!this.selectedMap || !this.selectedTeam || this.selectedTeam.status !== 0 || !this.exploreHours) {
        return false
      }
      if (this.hasMissingAuraSlot(this.selectedTeam)) {
        return false
      }
      if (this.getLowStarvationAuras(this.selectedTeam).length || this.getBattleStatusAuras(this.selectedTeam).length) {
        return false
      }
      const teamPower = this.calculateTeamPower(this.selectedTeam)
      return teamPower >= Number(this.selectedMap.minCb || 0)
    },
    powerMatchText() {
      if (!this.selectedMap || !this.selectedTeam) {
        return '待选择'
      }
      const power = this.calculateTeamPower(this.selectedTeam)
      if (power < Number(this.selectedMap.minCb || 0)) {
        return '战力不足'
      }
      if (power > Number(this.selectedMap.maxCb || Number.MAX_SAFE_INTEGER)) {
        return '战力充足，可直接出发'
      }
      return '匹配'
    },
    launchBlockingReason() {
      if (!this.selectedMap) {
        return '请先选择游历地图。'
      }
      if (!this.selectedTeam) {
        return '请先选择一个真灵小队。'
      }
      if (this.selectedTeam.status === 1) {
        return '当前小队正在游历中，不能重复派遣。'
      }
      if (this.selectedTeam.status === 2) {
        return '当前小队正在休整中，请等待休整结束。'
      }
      if (this.hasMissingAuraSlot(this.selectedTeam)) {
        return '当前小队未满 3 人，请先补满队伍。'
      }
      const lowAura = this.getLowStarvationAuras(this.selectedTeam)[0]
      if (lowAura) {
        return '真灵「' + lowAura.auraName + '」体力不足。'
      }
      const battleAura = this.getBattleStatusAuras(this.selectedTeam)[0]
      if (battleAura) {
        return '真灵「' + battleAura.auraName + '」处于出战状态。'
      }
      if (!this.exploreHours) {
        return '请先选择游历时长。'
      }
      if (false) {
        return '当前地图要求至少游历 ' + (this.selectedMap.minXiao || 1) + ' 小时。'
      }
      const power = this.calculateTeamPower(this.selectedTeam)
      if (power < Number(this.selectedMap.minCb || 0)) {
        return '当前小队战力低于地图推荐下限。'
      }
      return ''
    },
    selectedTeamIssues() {
      if (!this.selectedTeam) {
        return []
      }
      const dynamicIssues = []
      if (this.hasMissingAuraSlot(this.selectedTeam)) {
        dynamicIssues.push({ type: 'warning', text: '小队未满 3 个位置，过程表现和评分可能偏弱。' })
      }
      this.getLowStarvationAuras(this.selectedTeam).forEach(aura => {
        dynamicIssues.push({ type: 'warning', text: '真灵「' + aura.auraName + '」体力低于 ' + this.requiredStarvationCost + '。' })
      })
      this.getBattleStatusAuras(this.selectedTeam).forEach(aura => {
        dynamicIssues.push({ type: 'error', text: '真灵「' + aura.auraName + '」当前处于出战状态。' })
      })
      return dynamicIssues
      const issues = []
      if (this.hasMissingAuraSlot(this.selectedTeam)) {
        issues.push({ type: 'warning', text: '小队未满 3 个号位，过程表现和评分解释可能偏弱。' })
      }
      this.getLowStarvationAuras(this.selectedTeam).forEach(aura => {
        issues.push({ type: 'warning', text: '真灵「' + aura.auraName + '」体力低于 20。' })
      })
      this.getBattleStatusAuras(this.selectedTeam).forEach(aura => {
        issues.push({ type: 'error', text: '真灵「' + aura.auraName + '」当前处于出战状态。' })
      })
      return issues
    },
    processEmptyText() {
      if (!this.activeRecord) {
        return '选择一条记录查看过程详情'
      }
      if (this.activeRecord.status === 1) {
        return '该游历仍在进行中，过程记录可能尚未完全生成'
      }
      return '该记录暂无过程详情'
    },
    overallProcessSummary() {
      const process = this.currentProcess.find(item => {
        const variables = this.getVariables(item)
        return variables && (
          variables.phase2Score !== undefined ||
          variables.resultType !== undefined ||
          variables.scoreBreakdown
        )
      })
      return process ? this.getVariables(process) : null
    },
    overallScoreBreakdown() {
      if (!this.overallProcessSummary || !this.overallProcessSummary.scoreBreakdown) {
        return []
      }
      const score = this.overallProcessSummary.scoreBreakdown || {}
      const mapping = [
        { key: 'teamBaseScore', label: '队伍基础分' },
        { key: 'roleMatchScore', label: '位置匹配分' },
        { key: 'mapFitScore', label: '地图适配分' },
        { key: 'strategyAdjustScore', label: '策略修正分' },
        { key: 'randomAdjustScore', label: '随机浮动' },
        { key: 'phase2Score', label: '综合分' }
      ]
      return mapping
        .filter(item => score[item.key] !== undefined && score[item.key] !== null)
        .map(item => ({
          key: item.key,
          label: item.label,
          value: score[item.key]
        }))
    },
    headerEffectTags() {
      const summary = this.overallProcessSummary || {}
      const source = (
        summary.roleEffect ||
        summary.teamEffect ||
        summary.mapEffect
      )
        ? summary
        : this.currentProcess.map(item => this.getVariables(item)).find(variables => {
          return variables && (
            variables.roleEffect ||
            variables.teamEffect ||
            variables.mapEffect
          )
        }) || {}

      return [
        { key: 'roleEffect', text: source.roleEffect, color: 'blue' },
        { key: 'teamEffect', text: source.teamEffect, color: 'cyan' },
        { key: 'mapEffect', text: source.mapEffect, color: 'orange' }
      ].filter(item => item.text)
    },
    activeRecordStats() {
      if (!this.activeRecord || !this.activeRecord.recordStats) {
        return null
      }
      return typeof this.activeRecord.recordStats === 'string'
        ? this.safeParseObject(this.activeRecord.recordStats)
        : this.activeRecord.recordStats
    },
    activeRecord() {
      return this.exploreRecords.find(record => record.recordId === this.activeRecordId) || this.currentRecord
    },
    selectedFarmTravelRecord() {
      return this.farmTravelRecords.find(record => String(record.recordId) === String(this.selectedFarmTravelRecordId)) || null
    },
    farmTravelRecords() {
      return this.exploreRecords.filter(record => Number(record.status) === 1 || Number(record.status) === 2)
    },
    farmTravelNodes() {
      const points = [
        { left: 20, top: 34 }, { left: 34, top: 27 }, { left: 51, top: 28 },
        { left: 64, top: 37 }, { left: 22, top: 52 }, { left: 39, top: 55 },
        { left: 57, top: 51 }, { left: 73, top: 55 }, { left: 19, top: 70 },
        { left: 38, top: 70 }, { left: 57, top: 68 }, { left: 76, top: 72 }
      ]
      const sortedMaps = this.mapList.slice().sort((left, right) => String(left.mapId).localeCompare(String(right.mapId), 'zh-CN', { numeric: true }))
      const starIndex = sortedMaps.findIndex(map => String(map.mapName || '').includes('落星'))
      const blackIndex = sortedMaps.findIndex(map => String(map.mapName || '').includes('黑潮'))
      return sortedMaps.map((map, index) => {
        let pointIndex = index
        if (index === starIndex) pointIndex = blackIndex
        else if (index === blackIndex) pointIndex = starIndex
        const point = points[(pointIndex >= 0 ? pointIndex : index) % points.length]
        return Object.assign({}, map, { travelX: point.left, travelY: point.top })
      })
    },
    farmTravelRoads() {
      const origin = { left: 36, top: 43 }
      return this.farmTravelNodes.map((node, index) => ({
        id: 'farm-road-' + node.mapId,
        points: origin.left + ',' + origin.top + ' ' + node.travelX + ',' + node.travelY
      }))
    }
  },
  watch: {
    selectedStrategy(val) {
      localStorage.setItem('petExploreStrategy', val)
    }
  },
  mounted() {
    const visited = localStorage.getItem('petExploreVisited')
    if (!visited) {
      this.activeTab = 'guide'
      localStorage.setItem('petExploreVisited', '1')
    } else {
      this.activeTab = 'team'
    }
    this.loadData()
    this.updateTimer = setInterval(() => {
      this.$forceUpdate()
    }, 60000)
  },
  beforeDestroy() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer)
    }
  },
  methods: {
    farmTravelMapStyle(map, index) {
      const node = this.farmTravelNodes.find(item => String(item.mapId) === String(map && map.mapId))
      const point = node || this.farmTravelNodes[index]
      return { left: (point ? point.travelX : 36) + '%', top: (point ? point.travelY : 43) + '%' }
    },
    farmTravelTeamMapStyle(record, index) {
      const map = this.mapList.find(item => String(item.mapId) === String(record && record.mapId))
      const node = this.farmTravelNodes.find(item => String(item.mapId) === String(record && record.mapId))
      const base = node || { travelX: 36, travelY: 43 }
      const recordsAtMap = this.farmTravelRecordsForMap(map)
      const localIndex = recordsAtMap.findIndex(item => String(item.recordId) === String(record && record.recordId))
      const offsets = [
        { left: -8, top: -9 }, { left: 8, top: -9 },
        { left: -8, top: 9 }, { left: 8, top: 9 },
        { left: 0, top: -13 }, { left: 0, top: 13 }
      ]
      const offset = offsets[(localIndex >= 0 ? localIndex : index) % offsets.length]
      return {
        left: Math.max(8, Math.min(92, base.travelX + offset.left)) + '%',
        top: Math.max(8, Math.min(92, base.travelY + offset.top)) + '%'
      }
    },
    farmTravelRecordForMap(map) {
      if (!map) return null
      return this.farmTravelRecords.find(record => String(record.mapId) === String(map.mapId)) || null
    },
    farmTravelRecordsForMap(map) {
      if (!map) return []
      return this.farmTravelRecords.filter(record => String(record.mapId) === String(map.mapId))
    },
    async loadData() {
      await Promise.all([
        this.loadMaps(),
        this.loadAuras(),
        this.loadTeams(),
        this.loadExploreRecords()
      ])
    },
    async refreshData() {
      this.refreshing = true
      try {
        await this.loadData()
      } finally {
        this.refreshing = false
      }
    },
    async loadMaps() {
      try {
        const res = await this.$http.get('/gameAura/queryMapList')
        if (res.data.status === 200) {
          this.mapList = res.data.data || []
          const savedMapId = localStorage.getItem('petExploreMapId')
          if (savedMapId) {
            const saved = this.mapList.find(item => String(item.mapId) === String(savedMapId))
            if (saved) {
              this.selectMap(saved)
              return
            }
          }
          if (!this.selectedMapId && this.mapList.length) {
            this.selectMap(this.farmMode ? this.farmTravelNodes[0] : this.mapList[0])
          }
        }
      } catch (error) {
        this.$Message.error('获取地图列表失败：' + this.extractErrorMessage(error, '请稍后重试'))
      }
    },
    async loadAuras() {
      if (this.loadingAuras) {
        return
      }
      this.loadingAuras = true
      try {
        const res = await this.$http.post('/gameAura/getCharaPet?charaId=' + this.charaId)
        if (res.data.status === 200) {
          this.allAuras = (res.data.data || []).map(item => this.normalizeAura(item))
          this.availableAuras = this.allAuras
        }
      } catch (error) {
        this.$Message.error('获取真灵列表失败：' + this.extractErrorMessage(error, '请稍后重试'))
      } finally {
        this.loadingAuras = false
      }
    },
    async loadTeams() {
      try {
        const res = await this.$http.post('/gameAura/getAuraTeams?charaId=' + this.charaId)
        this.teamList = res.data.status === 200
          ? (res.data.data || []).map(team => this.normalizeTeam(team))
          : []
        if (!this.teamList.length) {
          this.selectedTeamId = null
          this.selectedTeam = null
          return
        }
        const matched = this.teamList.find(team => team.teamId === this.selectedTeamId) || this.teamList[0]
        this.selectedTeamId = matched.teamId
        this.selectedTeam = matched
      } catch (error) {
        this.teamList = []
        this.selectedTeam = null
        this.$Message.error('获取小队列表失败：' + this.extractErrorMessage(error, '请稍后重试'))
      }
    },
    async loadExploreRecords() {
      try {
        const res = await this.$http.post('/gameAura/getExploreRecords?charaId=' + this.charaId)
        this.exploreRecords = res.data.status === 200 ? (res.data.data || []) : []
        this.exploreRecords.sort((a, b) => new Date(b.startTime || b.createTime || 0) - new Date(a.startTime || a.createTime || 0))
        if (!this.exploreRecords.length) {
          this.activeRecordId = null
          this.currentRecord = null
          this.currentProcess = []
          return
        }
        const current = this.exploreRecords.find(record => record.recordId === this.activeRecordId)
        if (current) {
          this.currentRecord = current
        } else {
          this.selectRecord(this.exploreRecords[0])
        }
      } catch (error) {
        this.exploreRecords = []
        this.$Message.error('获取游历记录失败：' + this.extractErrorMessage(error, '请稍后重试'))
      }
    },
    async viewProcess(record) {
      const current = typeof record === 'string' ? this.exploreRecords.find(item => item.recordId === record) : record
      if (!current) {
        return
      }
      this.selectRecord(current)
      try {
        const res = await this.$http.post('/gameAura/getExploreProcess?recordId=' + current.recordId)
        if (res.data.status === 200) {
          this.currentProcess = res.data.data || []
          this.currentRecord = current
          if (!this.currentProcess.length) {
            this.$Message.info('该游历暂无详细过程记录')
          }
        } else {
          this.currentProcess = []
          this.$Message.info('该游历暂无详细过程记录')
        }
      } catch (error) {
        this.currentProcess = []
        this.$Message.error('获取游历过程失败：' + this.extractErrorMessage(error, '请稍后重试'))
      }
    },
    async startExplore() {
      if (!this.canStartExplore) {
        if (this.launchBlockingReason) {
          this.$Message.warning(this.launchBlockingReason)
        }
        return
      }
      this.exploreLoading = true
      try {
        const url = '/gameAura/startExplore?charaId=' +
          this.charaId +
          '&teamId=' + this.selectedTeamId +
          '&mapId=' + this.selectedMapId +
          '&hours=' + this.exploreHours +
          '&strategyType=' + this.selectedStrategy
        const res = await this.$http.post(url)
        if (res.data.status === 200) {
          this.$Message.success(res.data.msg || '游历开始成功')
          await Promise.all([this.loadTeams(), this.loadExploreRecords()])
        } else {
          this.$Message.error(res.data.msg || '开始游历失败')
        }
      } catch (error) {
        this.$Message.error('开始游历失败：' + this.extractErrorMessage(error, '请检查当前小队状态'))
      } finally {
        this.exploreLoading = false
      }
    },
    async completeExplore(recordId) {
      this.completingRecordId = recordId
      try {
        const res = await this.$http.post('/gameAura/completeExplore?charaId=' + this.charaId + '&recordId=' + recordId)
        if (res.data.status === 200) {
          const payload = res.data.data || {}
          this.rewardResult = {
            rewards: this.normalizeRewards(payload.rewards),
            auraExpGains: this.normalizeAuraExpGains(payload.auraExpGains),
            restTime: payload.restTime ? payload.restTime : 0,
            message: this.normalizeRewardMessage(payload.message)
          }
          this.rewardModalVisible = true
          await Promise.all([this.loadExploreRecords(), this.loadTeams()])
        } else {
          this.$Message.error(res.data.msg || '领取奖励失败')
        }
      } catch (error) {
        this.$Message.error('领取奖励失败：' + this.extractErrorMessage(error, '请稍后重试'))
      } finally {
        this.completingRecordId = null
      }
    },
    async openAuraMaterialBag() {
      this.auraMaterialBagVisible = true
      if (!this.auraMaterialBagList.length) {
        await this.loadAuraMaterialBag()
      }
    },
    async loadAuraMaterialBag() {
      this.auraMaterialBagLoading = true
      try {
        const res = await this.$http.post('/gameAura/getAuraMaterialBag?charaId=' + this.charaId)
        if (res.data.status === 200) {
          this.auraMaterialBagList = Array.isArray(res.data.data) ? res.data.data : []
        } else {
          this.auraMaterialBagList = []
          this.$Message.error(res.data.msg || '获取真灵包裹失败')
        }
      } catch (error) {
        this.auraMaterialBagList = []
        this.$Message.error('获取真灵包裹失败：' + this.extractErrorMessage(error, '请稍后重试'))
      } finally {
        this.auraMaterialBagLoading = false
      }
    },
    selectMap(map) {
      this.selectedMapId = map.mapId
      this.selectedMap = map
      this.selectedFarmTravelRecordId = null
      localStorage.setItem('petExploreMapId', map.mapId)
    },
    selectRecord(record) {
      this.activeRecordId = record.recordId
      this.currentRecord = record
    },
    selectFarmTravelRecord(record) {
      if (!record) return
      this.selectRecord(record)
      const map = this.mapList.find(item => String(item.mapId) === String(record.mapId))
      if (map) this.selectMap(map)
      this.selectedFarmTravelRecordId = record.recordId
    },
    clearFarmTravelSelection() {
      this.selectedMapId = null
      this.selectedMap = null
      this.selectedFarmTravelRecordId = null
    },
    setExploreHours(hours) {
      this.exploreHours = hours
      localStorage.setItem('petExploreHours', hours)
    },
    getStrategyLabel(strategyValue) {
      const strategy = this.strategyOptions.find(item => item.value === strategyValue)
      return strategy ? strategy.label : '均衡'
    },
    findMapByRecommendation(plan) {
      if (!plan || !plan.mapName) {
        return null
      }
      return this.mapList.find(map => String(map.mapName || '').includes(plan.mapName)) || null
    },
    isRecommendationActive(plan) {
      const map = this.findMapByRecommendation(plan)
      return !!map
        && String(this.selectedMapId) === String(map.mapId)
        && Number(this.exploreHours) === Number(plan.hours)
        && this.selectedStrategy === plan.strategy
    },
    applyExploreRecommendation(plan) {
      const map = this.findMapByRecommendation(plan)
      if (!map) {
        this.$Message.warning('当前地图列表里没有找到：' + plan.mapName)
        return
      }
      this.selectMap(map)
      this.setExploreHours(plan.hours)
      this.selectedStrategy = plan.strategy
      if (!this.farmMode) this.activeTab = 'team'
      this.$Message.success('已套用：' + plan.title)
    },
    applyAwakeningMaterialGuide(material) {
      this.applyExploreRecommendation({
        key: 'material-' + material.itemId,
        title: material.itemName,
        mapName: material.mapName,
        hours: material.hours,
        strategy: material.strategy
      })
    },
    onTeamChange(teamId) {
      this.selectedTeamId = teamId
      this.selectedTeam = this.teamList.find(team => String(team.teamId) === String(teamId)) || null
    },
    createTeam() {
      this.editingTeam = null
      this.teamForm = { teamName: '', selectedAuras: [] }
      this.showCreateTeam = true
    },
    manageTeam() {
      if (this.selectedTeam && this.selectedTeam.status === 0) {
        this.editTeam(this.selectedTeam)
        return
      }
      this.editingTeam = null
      this.teamForm = { teamName: '', selectedAuras: [] }
      this.showCreateTeam = true
    },
    editTeam(team) {
      this.editingTeam = team
      this.teamForm = {
        teamName: team.teamName || '',
        selectedAuras: this.getTeamAuraIds(team)
      }
      this.showCreateTeam = true
    },
    cancelCreateTeam() {
      this.showCreateTeam = false
      this.editingTeam = null
      this.teamForm = { teamName: '', selectedAuras: [] }
    },
    async saveTeam() {
      if (!this.teamForm.teamName) {
        this.$Message.warning('请输入小队名称')
        return
      }
      if (!this.teamForm.selectedAuras.length) {
        this.$Message.warning('请选择真灵')
        return
      }
      this.teamLoading = true
      try {
        let res
        if (this.editingTeam) {
          const url = '/gameAura/updateAuraTeam?charaId=' +
            this.charaId +
            '&teamId=' + this.editingTeam.teamId +
            '&teamName=' + encodeURIComponent(this.teamForm.teamName) +
            '&auraIds=' + this.teamForm.selectedAuras.join(',')
          res = await this.$http.post(url)
        } else {
          const url = '/gameAura/createTeam?charaId=' +
            this.charaId +
            '&teamName=' + encodeURIComponent(this.teamForm.teamName) +
            '&auraIds=' + this.teamForm.selectedAuras.join(',')
          res = await this.$http.post(url)
        }
        if (res.data.status === 200) {
          this.$Message.success(this.editingTeam ? '小队更新成功' : '小队创建成功')
          this.cancelCreateTeam()
          await Promise.all([this.loadAuras(), this.loadTeams()])
        } else {
          this.$Message.error(res.data.msg || '保存小队失败')
        }
      } catch (error) {
        this.$Message.error('保存小队失败：' + this.extractErrorMessage(error, '请检查队伍配置'))
      } finally {
        this.teamLoading = false
      }
    },
    async deleteTeam() {
      if (!this.editingTeam) {
        return
      }
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定删除小队「' + this.editingTeam.teamName + '」吗？',
        onOk: async () => {
          this.teamLoading = true
          try {
            const res = await this.$http.post('/gameAura/deleteAuraTeam?charaId=' + this.charaId + '&teamId=' + this.editingTeam.teamId)
            if (res.data.status === 200) {
              this.$Message.success('小队删除成功')
              this.cancelCreateTeam()
              await Promise.all([this.loadTeams(), this.loadAuras()])
            } else {
              this.$Message.error(res.data.msg || '删除小队失败')
            }
          } catch (error) {
            this.$Message.error('删除小队失败：' + this.extractErrorMessage(error, '请稍后重试'))
          } finally {
            this.teamLoading = false
          }
        }
      })
    },
    isAuraInOtherTeam(auraId) {
      return this.teamList.some(team => {
        if (this.editingTeam && team.teamId === this.editingTeam.teamId) {
          return false
        }
        return [team.auraId1, team.auraId2, team.auraId3].includes(auraId)
      })
    },
    isAuraDisabledInForm(auraId) {
      return (
        (this.teamForm.selectedAuras.length >= this.maxTeamSize && !this.teamForm.selectedAuras.includes(auraId)) ||
        this.isAuraInOtherTeam(auraId) ||
        (Number((this.allAuras.find(item => String(item.auraLanId) === String(auraId)) || {}).auraStatus || 0) === 1 && !this.teamForm.selectedAuras.includes(auraId))
      )
    },
    hasMissingAuraSlot(team) {
      return this.getTeamAuraIds(team).length < this.maxTeamSize
    },
    getLowStarvationAuras(team) {
      const requiredCost = this.requiredStarvationCost
      return this.roleSlots
        .map(slot => this.getTeamAuraBySlot(team, slot.slot))
        .filter(aura => aura && Number(aura.auraStarvationValue || 0) < requiredCost)
    },
    getMapHourlyCost(map) {
      return Number((map && map.minXiao) || 1)
    },
    getBattleStatusAuras(team) {
      return this.roleSlots
        .map(slot => this.getTeamAuraBySlot(team, slot.slot))
        .filter(aura => aura && Number(aura.auraStatus || 0) === 1)
    },
    getTeamAuraIds(team) {
      return [team.auraId1, team.auraId2, team.auraId3].filter(Boolean)
    },
    getTeamAuraBySlot(team, slot) {
      const auraField = 'aura' + slot
      const auraIdField = 'auraId' + slot
      if (team && team[auraField] && typeof team[auraField] === 'object') {
        return this.normalizeAura(team[auraField], team[auraIdField])
      }
      return this.allAuras.find(item => item.auraLanId === team[auraIdField] || item.auraId === team[auraIdField]) || null
    },
    normalizeTeam(team) {
      if (!team || typeof team !== 'object') {
        return team
      }
      const normalized = { ...team }
      this.roleSlots.forEach(slot => {
        const auraField = 'aura' + slot.slot
        const auraIdField = 'auraId' + slot.slot
        if (normalized[auraField] && typeof normalized[auraField] === 'object') {
          normalized[auraField] = this.normalizeAura(normalized[auraField], normalized[auraIdField])
        }
      })
      return normalized
    },
    normalizeAura(aura, fallbackId) {
      if (!aura || typeof aura !== 'object') {
        return aura
      }
      return {
        ...aura,
        auraId: aura.auraId || aura.auraLanId || fallbackId || aura.petRandomId || aura.petId,
        auraLanId: aura.auraLanId || aura.auraId || fallbackId || aura.petRandomId || aura.petId,
        auraName: aura.auraName || aura.petRandomName || aura.petName || aura.name,
        auraRace: aura.auraRace || aura.petRandomRace || aura.petRace,
        auraGrade: aura.auraGrade !== undefined && aura.auraGrade !== null ? aura.auraGrade : aura.petRandomGrade,
        auraAptitude: aura.auraAptitude !== undefined && aura.auraAptitude !== null ? aura.auraAptitude : aura.petRandomAptitude,
        auraType: aura.auraType !== undefined && aura.auraType !== null ? aura.auraType : aura.petRandomType,
        auraElement: aura.auraElement !== undefined && aura.auraElement !== null ? aura.auraElement : aura.petRandomElement,
        auraIcon: aura.auraIcon || aura.petRandomIcon || aura.icon,
        auraDesci: aura.auraDesci || aura.petRandomDesci || aura.description,
        auraLevel: aura.auraLevel !== undefined && aura.auraLevel !== null ? aura.auraLevel : aura.petRandomLevel,
        auraLevelCap: aura.auraLevelCap !== undefined && aura.auraLevelCap !== null ? aura.auraLevelCap : aura.petRandomLevelCap,
        auraStarvationValue: aura.auraStarvationValue !== undefined && aura.auraStarvationValue !== null ? aura.auraStarvationValue : aura.petRandomStarvationValue,
        auraLife: aura.auraLife !== undefined && aura.auraLife !== null ? aura.auraLife : aura.petRandomLife,
        auraMana: aura.auraMana !== undefined && aura.auraMana !== null ? aura.auraMana : aura.petRandomMana,
        auraAttack: aura.auraAttack !== undefined && aura.auraAttack !== null ? aura.auraAttack : aura.petRandomAttack,
        auraMagicAttack: aura.auraMagicAttack !== undefined && aura.auraMagicAttack !== null ? aura.auraMagicAttack : aura.petRandomMagicAttack,
        auraDefence: aura.auraDefence !== undefined && aura.auraDefence !== null ? aura.auraDefence : aura.petRandomDefence,
        auraMagicDefence: aura.auraMagicDefence !== undefined && aura.auraMagicDefence !== null ? aura.auraMagicDefence : aura.petRandomMagicDefence,
        auraPhysique: aura.auraPhysique !== undefined && aura.auraPhysique !== null ? aura.auraPhysique : aura.petRandomPhysique,
        auraDexterity: aura.auraDexterity !== undefined && aura.auraDexterity !== null ? aura.auraDexterity : aura.petRandomDexterity,
        auraSpirit: aura.auraSpirit !== undefined && aura.auraSpirit !== null ? aura.auraSpirit : aura.petRandomSpirit,
        auraActionSpeed: aura.auraActionSpeed !== undefined && aura.auraActionSpeed !== null ? aura.auraActionSpeed : aura.petRandomActionSpeed,
        auraExplode: aura.auraExplode !== undefined && aura.auraExplode !== null ? aura.auraExplode : aura.petRandomExplode,
        auraCalDamage: aura.auraCalDamage !== undefined && aura.auraCalDamage !== null ? aura.auraCalDamage : aura.petRandomCriticalDamage,
        auraHitRate: aura.auraHitRate !== undefined && aura.auraHitRate !== null ? aura.auraHitRate : aura.petRandomHitRate,
        auraEvade: aura.auraEvade !== undefined && aura.auraEvade !== null ? aura.auraEvade : aura.petRandomEvade,
        auraCombo: aura.auraCombo !== undefined && aura.auraCombo !== null ? aura.auraCombo : aura.petRandomCombo,
        auraCounter: aura.auraCounter !== undefined && aura.auraCounter !== null ? aura.auraCounter : aura.petRandomCounter,
        auraArmor: aura.auraArmor !== undefined && aura.auraArmor !== null ? aura.auraArmor : aura.petRandomArmor,
        auraCombot: aura.auraCombot !== undefined && aura.auraCombot !== null ? aura.auraCombot : aura.petRandomCombatPower,
        auraExp: aura.auraExp !== undefined && aura.auraExp !== null ? aura.auraExp : aura.petRandomExp,
        auraUpgradeExp: aura.auraUpgradeExp !== undefined && aura.auraUpgradeExp !== null ? aura.auraUpgradeExp : aura.petRandomUpgradeExp,
        auraSkillNum: aura.auraSkillNum !== undefined && aura.auraSkillNum !== null ? aura.auraSkillNum : aura.petRandomSkillNum,
        auraStatus: aura.auraStatus !== undefined && aura.auraStatus !== null ? aura.auraStatus : aura.status
      }
    },
    calculateTeamPower(team) {
      return this.roleSlots.reduce((sum, slot) => {
        const aura = this.getTeamAuraBySlot(team, slot.slot)
        return sum + Number(aura && aura.auraCombot ? aura.auraCombot : 0)
      }, 0)
    },
    calculateTeamLevel(team) {
      const auras = this.roleSlots
        .map(slot => this.getTeamAuraBySlot(team, slot.slot))
        .filter(Boolean)
      if (!auras.length) {
        return 0
      }
      const total = auras.reduce((sum, aura) => sum + Number(aura.auraLevel || 0), 0)
      return Math.round(total / auras.length)
    },
    getRecordMapName(record) {
      if (record.exploreMap && record.exploreMap.mapName) {
        return record.exploreMap.mapName
      }
      const map = this.mapList.find(item => item.mapId === record.mapId)
      return map ? map.mapName : '未知地图'
    },
    getRecordTeamName(record) {
      if (record.auraTeam && record.auraTeam.teamName) {
        return record.auraTeam.teamName
      }
      const team = this.teamList.find(item => item.teamId === record.teamId)
      return team ? team.teamName : '未知小队'
    },
    getRecordHint(record) {
      if (!record) {
        return ''
      }
      if (record.status === 1) {
        return '预计结束时间：' + this.formatTime(record.endTime)
      }
      if (record.status === 2) {
        return '本次游历已完成，可以领取奖励。'
      }
      if (record.status === 3) {
        const rewards = this.normalizeRewards(record.rewards)
        return rewards.length ? '已领取奖励，共 ' + rewards.length + ' 项。' : '奖励已领取。'
      }
      return '当前记录状态待确认。'
    },
    getMapExploreProgress(record) {
      if (!record) {
        return 0
      }
      if (record.mapExploreProgress !== undefined && record.mapExploreProgress !== null) {
        return record.mapExploreProgress
      }
      const stats = record.recordStats ? (typeof record.recordStats === 'string' ? this.safeParseObject(record.recordStats) : record.recordStats) : null
      return stats && stats.mapExploreProgress ? stats.mapExploreProgress : 0
    },
    getMapExploreGain(record) {
      if (!record) {
        return 0
      }
      if (record.mapExploreGain !== undefined && record.mapExploreGain !== null) {
        return record.mapExploreGain
      }
      const stats = record.recordStats ? (typeof record.recordStats === 'string' ? this.safeParseObject(record.recordStats) : record.recordStats) : null
      return stats && stats.mapExploreGain ? stats.mapExploreGain : 0
    },
    getVariables(process) {
      if (!process || process.variables === undefined || process.variables === null) {
        return {}
      }
      if (typeof process.variables === 'object') {
        return process.variables
      }
      if (typeof process.variables === 'string') {
        try {
          return JSON.parse(process.variables)
        } catch (error) {
          return {}
        }
      }
      return {}
    },
    getScoreBreakdown(process) {
      const variables = this.getVariables(process)
      const score = variables.scoreBreakdown || {}
      const mapping = [
        { key: 'teamBaseScore', label: '队伍基础分' },
        { key: 'roleMatchScore', label: '位置匹配分' },
        { key: 'mapFitScore', label: '地图适配分' },
        { key: 'strategyAdjustScore', label: '策略修正分' },
        { key: 'randomAdjustScore', label: '随机浮动' },
        { key: 'phase2Score', label: '综合分' }
      ]
      return mapping
        .filter(item => score[item.key] !== undefined && score[item.key] !== null)
        .map(item => ({
          key: item.key,
          label: item.label,
          value: score[item.key]
        }))
    },
    getRecordStatusText(status) {
      const texts = { 1: '进行中', 2: '可领取', 3: '已领取' }
      return texts[status] || '未知状态'
    },
    getRecordStatusColor(status) {
      const colors = { 1: 'blue', 2: 'orange', 3: 'green' }
      return colors[status] || 'default'
    },
    getTeamStatusText(status) {
      const texts = { 0: '空闲', 1: '游历中', 2: '休整中' }
      return texts[status] || '未知状态'
    },
    getTeamStatusColor(status) {
      const colors = { 0: 'green', 1: 'blue', 2: 'orange' }
      return colors[status] || 'default'
    },
    getMapTypeText(mapType) {
      const texts = {
        1: '灵山',
        2: '险地',
        3: '秘境'
      }
      return texts[mapType] || '游历区域'
    },
    getEventTypeText(eventType) {
      const texts = {
        WEATHER: '天象',
        BATTLE: '战斗',
        DISCOVERY: '发现',
        SERENDIPITY: '奇遇',
        RESOURCE: '收获'
      }
      return texts[eventType] || eventType || '过程事件'
    },
    getResultTypeText(resultType) {
      const texts = {
        GREAT_SUCCESS: '大成功',
        SUCCESS: '成功',
        GOOD: '良好',
        NORMAL: '普通',
        MINOR_FAIL: '小挫'
      }
      return texts[resultType] || (resultType || '未判定')
    },
    getRarityColor(rarity) {
      const colors = {
        1: 'default',
        2: 'blue',
        3: 'green',
        4: 'orange',
        5: 'red'
      }
      return colors[rarity] || 'default'
    },
    getGradeName(grade) {
      const texts = {
        1: '凡品',
        2: '良品',
        3: '上品',
        4: '珍品',
        5: '绝品'
      }
      return texts[grade] || '未知'
    },
    getGradeColor(grade) {
      const colors = {
        1: 'default',
        2: 'blue',
        3: 'green',
        4: 'orange',
        5: 'red'
      }
      return colors[grade] || 'default'
    },
    getTypeName(type) {
      const texts = {
        1: '攻击',
        2: '防御',
        3: '辅助',
        4: '均衡'
      }
      return texts[type] || '未知'
    },
    getTypeColor(type) {
      const colors = {
        1: 'red',
        2: 'blue',
        3: 'cyan',
        4: 'gold'
      }
      return colors[type] || 'default'
    },
    formatCombatPower(value) {
      const amount = Number(value || 0)
      return amount.toLocaleString()
    },
    formatScore(value) {
      if (value === undefined || value === null || value === '') {
        return '-'
      }
      return Number(value).toFixed(1)
    },
    formatTime(time) {
      if (!time) {
        return '-'
      }
      const raw = String(time).trim()
      const hasTimezone = /([+-]\d{4}|[+-]\d{2}:\d{2}|Z)$/i.test(raw) || /T/.test(raw)
      let normalized = raw
      if (hasTimezone) {
        normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
      } else {
        normalized = normalized.replace(/-/g, '/')
      }
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) {
        const fallback = raw
          .replace('T', ' ')
          .replace(/\.\d+/, '')
          .replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
        const retry = new Date(fallback)
        if (Number.isNaN(retry.getTime())) {
          return fallback
        }
        return this.formatTime(retry.toISOString())
      }
      let year
      let month
      let day
      let hour
      let minute
      let second
      if (hasTimezone) {
        const utc8Date = new Date(date.getTime() + 8 * 60 * 60 * 1000)
        year = utc8Date.getUTCFullYear()
        month = String(utc8Date.getUTCMonth() + 1).padStart(2, '0')
        day = String(utc8Date.getUTCDate()).padStart(2, '0')
        hour = String(utc8Date.getUTCHours()).padStart(2, '0')
        minute = String(utc8Date.getUTCMinutes()).padStart(2, '0')
        second = String(utc8Date.getUTCSeconds()).padStart(2, '0')
      } else {
        year = date.getFullYear()
        month = String(date.getMonth() + 1).padStart(2, '0')
        day = String(date.getDate()).padStart(2, '0')
        hour = String(date.getHours()).padStart(2, '0')
        minute = String(date.getMinutes()).padStart(2, '0')
        second = String(date.getSeconds()).padStart(2, '0')
      }
      return year + '-' + month + '-' + day + ' ' + hour + ':' + minute + ':' + second
    },
    normalizeRewards(rewards) {
      if (!rewards) {
        return []
      }
      if (Array.isArray(rewards)) {
        return rewards
      }
      if (typeof rewards === 'string') {
        try {
          const parsed = JSON.parse(rewards)
          return Array.isArray(parsed) ? parsed : []
        } catch (error) {
          return []
        }
      }
      return []
    },
    normalizeAuraExpGains(auraExpGains) {
      if (!auraExpGains) {
        return []
      }
      if (Array.isArray(auraExpGains)) {
        return auraExpGains
      }
      if (typeof auraExpGains === 'string') {
        try {
          const parsed = JSON.parse(auraExpGains)
          return Array.isArray(parsed) ? parsed : []
        } catch (error) {
          return []
        }
      }
      return []
    },
    safeParseObject(text) {
      if (!text || typeof text !== 'string') {
        return null
      }
      try {
        return JSON.parse(text)
      } catch (error) {
        return null
      }
    },
    normalizeRewardMessage(message) {
      const fallback = '游历完成，材料已存入真灵包裹，小队已恢复空闲。'
      if (!message) {
        return fallback
      }
      return String(message)
        .replace(/奖励已发放到角色背包/g, '材料已存入真灵包裹')
        .replace(/奖励已发放至角色背包/g, '材料已存入真灵包裹')
        .replace(/奖励已发送到角色背包/g, '材料已存入真灵包裹')
    },
    extractErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        const data = error.response.data
        if (typeof data === 'string') {
          return data
        }
        if (data.msg) {
          return data.msg
        }
        if (data.message) {
          return data.message
        }
      }
      if (error && error.message) {
        return error.message
      }
      return fallback || '未知错误'
    },
    getCookie(name) {
      const arr = document.cookie.match(new RegExp('(^| )' + name + '=([^;]*)(;|$)'))
      return arr ? decodeURIComponent(arr[2]) : null
    }
  }
}
</script>

<style scoped>
.farm-travel-choice-list{grid-template-columns:repeat(5,minmax(0,1fr))!important;gap:4px}
.explore-page {
  padding: 18px;
  background:
    radial-gradient(circle at top right, rgba(81, 148, 255, 0.12), transparent 28%),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
}



/* ===== 主布局 ===== */
.main-layout {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}

/* ===== 左侧导航 ===== */
.side-nav {
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: sticky;
  top: 18px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e8eef5;
  cursor: pointer;
  transition: all 0.22s ease;
  font-weight: 500;
  color: #55769f;
  box-shadow: 0 2px 8px rgba(64, 110, 180, 0.04);
}

.nav-item:hover {
  border-color: #b3cef0;
  background: #f8fbff;
  color: #2f6fd1;
}

.nav-item.active {
  border-color: #4c87e3;
  background: linear-gradient(135deg, #edf4ff, #dce9fa);
  color: #1a4f9e;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(76, 135, 227, 0.18);
}

.nav-item .ivu-tag {
  margin-left: auto;
}

/* ===== 右侧内容区 ===== */
.content-area {
  flex: 1;
  min-width: 0;
}

.tab-content {
  animation: fadeSlideIn 0.28s ease;
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== 面板通用 ===== */
.panel-card {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(64, 110, 180, 0.05);
}

.panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

.panel-subtitle {
  font-size: 11px;
  font-weight: 400;
  color: #8a9db5;
}

.panel-alert {
  margin-top: 8px;
}

/* ===== 游历说明 ===== */
.guide-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.guide-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f3f67;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #eef3f9;
}

.guide-section > p {
  margin: 0 0 12px;
  color: #49627f;
  line-height: 1.7;
}

.guide-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.guide-grid-2 {
  grid-template-columns: repeat(2, 1fr);
}

.guide-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid #e8eef5;
}

.guide-card strong {
  color: #1f3f67;
  font-size: 14px;
}

.guide-card p {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6d84a3;
  line-height: 1.65;
}

.guide-card-num {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #4c87e3;
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.guide-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fffdf5;
  border: 1px solid #f0e6c0;
  font-size: 12px;
  color: #8a7552;
  line-height: 1.6;
}

/* ===== 地图卡片网格 ===== */
.map-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
}

.map-block {
  position: relative;
  padding: 8px 10px;
  border: 1px solid #dce7f7;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.22s ease;
}

.map-block:hover {
  border-color: #b3cef0;
  box-shadow: 0 4px 16px rgba(76, 135, 227, 0.1);
  transform: translateY(-2px);
}

.map-block.active {
  border-color: #4c87e3;
  background: linear-gradient(135deg, #f3f8ff, #eaf2fc);
  box-shadow: 0 6px 20px rgba(76, 135, 227, 0.16);
}

.map-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.map-block-name {
  font-weight: 600;
  font-size: 13px;
  color: #1f3f67;
}

.map-block-type {
  font-size: 11px;
  color: #6d84a3;
  margin-top: 2px;
}

.map-block-desc {
  margin: 4px 0 0;
  line-height: 1.4;
  color: #49627f;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-block-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eef3f9;
  font-size: 11px;
  color: #6a82a1;
}

.map-block-foot strong {
  color: #2d527d;
  font-weight: 600;
}

.map-block-check {
  position: absolute;
  top: 10px;
  right: 10px;
}

/* ===== 策略卡片 ===== */
.strategy-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
}

.strategy-card {
  flex: 1;
  min-width: 120px;
  padding: 6px 10px;
  border: 1px solid #dce7f7;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.strategy-card:hover {
  border-color: #b3cef0;
  background: #fafcff;
}

.strategy-card.active {
  border-color: #66a3ff;
  background: #f3f8ff;
  box-shadow: 0 4px 14px rgba(76, 135, 227, 0.12);
}

.strategy-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.strategy-name {
  font-weight: 600;
  font-size: 13px;
  color: #23456d;
}

.strategy-desc {
  margin: 2px 0 0 22px;
  font-size: 11px;
  line-height: 1.4;
  color: #6f87a6;
}

/* ===== 时长选择 ===== */
.hours-block {
  margin-top: 0;
}

.hours-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hours-list .ivu-btn {
  min-width: 100px;
  border-radius: 12px;
  background: #ffffff;
  border-color: #dce7f7;
  color: #55769f;
  box-shadow: none;
  font-weight: 500;
}

.hours-list .ivu-btn-primary {
  background: #f3f8ff;
  border-color: #66a3ff;
  color: #245ba5;
  font-weight: 600;
}

.hours-hint {
  margin-top: 12px;
  font-size: 13px;
  color: #6d84a3;
}

.hours-hint strong {
  color: #2d527d;
}

/* ===== 推荐搭配 ===== */
.recommend-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.recommend-card {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #dce7f7;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recommend-card:hover {
  border-color: #b3cef0;
  background: #fafcff;
}

.recommend-card.active {
  border-color: #66a3ff;
  background: #f3f8ff;
  box-shadow: 0 4px 14px rgba(76, 135, 227, 0.12);
}

.recommend-card.disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.recommend-head,
.material-guide-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.recommend-head strong {
  min-width: 0;
  color: #1f3f67;
  font-size: 14px;
}

.recommend-route {
  margin-top: 6px;
  color: #2d8cf0;
  font-size: 12px;
}

.recommend-card p {
  margin: 6px 0 0;
  color: #49627f;
  font-size: 12px;
  line-height: 1.5;
}

.recommend-yield {
  margin-top: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  color: #7a4c00;
  background: #fff7e6;
  font-size: 12px;
  line-height: 1.45;
}

.material-guide {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #dce7f7;
  border-radius: 12px;
  background: #fbfdff;
}

.material-guide-title {
  margin-bottom: 8px;
  color: #2c527c;
  font-weight: 700;
}

.material-guide-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.material-guide-row {
  min-width: 0;
  padding: 8px;
  border: 1px solid #edf2f8;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
}

.material-guide-row:hover {
  border-color: #b3cef0;
  background: #f3f8ff;
}

.material-guide-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.material-guide-main strong {
  color: #1f3f67;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-guide-main span {
  color: #6d84a3;
  font-size: 12px;
}

/* ===== 队伍出发摘要条 ===== */
.launch-summary-bar {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 14px 20px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f0f6ff, #e6f0fc);
  border: 1px solid #d4e2f5;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.summary-item:hover {
  background: rgba(47, 111, 209, 0.08);
}

.summary-label {
  font-size: 12px;
  color: #6d84a3;
}

.summary-item strong {
  color: #1f3f67;
  font-weight: 600;
}

.summary-item .ivu-icon {
  color: #8a9db5;
  margin-left: 2px;
}

.summary-divider {
  width: 1px;
  height: 24px;
  background: #cdd9e8;
  margin: 0 8px;
}

/* ===== 出发确认面板 ===== */
.launch-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef3f9;
}

.launch-section-title {
  font-weight: 600;
  color: #2c527c;
  margin-bottom: 12px;
  font-size: 14px;
}
.launch-confirm-layout {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.launch-check {
  display: flex;
  gap: 24px;
  padding: 14px 20px;
  border-radius: 12px;
  background: #fafcff;
  border: 1px solid #eef3f9;
}

.check-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
}

.check-row span {
  color: #8a9db5;
}

.check-row strong {
  color: #2d527d;
  font-size: 14px;
}

.launch-alerts {
  flex: 1;
  min-width: 200px;
}

.launch-alerts .ivu-alert {
  margin-bottom: 0;
}

.launch-btn {
  flex-shrink: 0;
  min-width: 140px;
  height: 44px;
  border-radius: 12px;
}

/* ===== 记录标签切换 ===== */
.record-tabs {
  display: flex;
  gap: 4px;
  background: #eef3f9;
  border-radius: 10px;
  padding: 3px;
}

.record-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  color: #55769f;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.record-tab:hover {
  color: #2f6fd1;
}

.record-tab.active {
  background: #ffffff;
  color: #1a4f9e;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(64, 110, 180, 0.1);
}

.record-scroll-area {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.record-scroll-area::-webkit-scrollbar {
  width: 5px;
}

.record-scroll-area::-webkit-scrollbar-track {
  background: transparent;
}

.record-scroll-area::-webkit-scrollbar-thumb {
  background: #cdd9e8;
  border-radius: 999px;
}

.record-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #a3b8d4;
}

/* ===== 记录列表 ===== */
.record-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.record-card {
  padding: 14px;
  cursor: pointer;
  border: 1px solid #dce7f7;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.record-card.active {
  border-color: #66a3ff;
  background: #f3f8ff;
  box-shadow: 0 4px 14px rgba(76, 135, 227, 0.12);
}

.record-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.record-title {
  font-weight: 700;
  color: #1f3f67;
}

.record-time {
  font-size: 12px;
  color: #6d84a3;
}

.record-desc {
  margin: 10px 0 0;
  line-height: 1.65;
  color: #49627f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.record-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6d84a3;
}

.record-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  font-size: 12px;
  color: #6a82a1;
}

.record-actions {
  display: flex;
  gap: 8px;
}

/* ===== 小队配置 ===== */
.team-selector {
  width: 180px;
}

.team-selector .ivu-select-selection {
  background: #ffffff;
  border-radius: 10px;
  border-color: #dce7f7;
  box-shadow: none;
}

.team-selector-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.team-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.team-name {
  font-weight: 700;
  color: #1f3f67;
}

.team-subline {
  font-size: 12px;
  color: #6d84a3;
}

.slot-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.slot-block {
  padding: 12px 10px 10px;
  border: 1px solid #dce7f7;
  border-radius: 10px;
  background: #fafcff;
  text-align: center;
  transition: all 0.2s ease;
}

.slot-block:hover {
  border-color: #b3cef0;
  box-shadow: 0 2px 8px rgba(76, 135, 227, 0.05);
}

.slot-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 2px;
}

.slot-index {
  font-weight: 700;
  font-size: 12px;
  color: #2d527d;
}

.slot-block-role {
  font-size: 11px;
  color: #7390b5;
  margin-bottom: 8px;
}

.slot-block-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.slot-block-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  background: #f0e6d0;
}

.slot-block-name {
  font-weight: 600;
  color: #3d2c12;
  font-size: 12px;
}

.slot-block-stats {
  font-size: 11px;
  color: #6d84a3;
}

.slot-block-empty {
  padding: 12px 0;
  color: #a3b8d4;
  font-size: 12px;
}

.empty-action {
  padding: 24px 12px;
  text-align: center;
  color: #6f87a7;
}

/* ===== 过程战报 ===== */
.process-header-card {
  padding: 14px 16px;
  border: 1px solid #dce7f7;
  border-radius: 14px;
  background: #f7fbff;
}

.process-summary-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.process-record-title {
  font-weight: 600;
  color: #3d2c12;
}

.process-record-meta {
  font-size: 12px;
  color: #6d84a3;
}

.process-record-desc {
  margin: 10px 0 0;
  line-height: 1.65;
  color: #49627f;
}

.process-overview {
  margin-top: 12px;
}

.process-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 14px;
}

.process-card {
  padding: 14px;
  border: 1px solid #dce7f7;
  border-radius: 14px;
  background: #ffffff;
}

.process-head {
  width: 100%;
}

.process-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.process-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.process-event,
.process-result-tag {
  color: #58789f;
}

.process-desc-line {
  margin: 8px 0 0;
  line-height: 1.6;
  color: #49627f;
  white-space: normal;
  word-break: break-word;
}

/* ===== 通用小组件 ===== */
.expand-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.expand-item,
.score-item {
  padding: 10px 12px;
  border-radius: 12px;
  background: #fbf6ea;
  border: 1px solid #efe2c7;
}

.expand-item span,
.score-item span {
  display: block;
  font-size: 12px;
  color: #8a7552;
}

.expand-item strong,
.score-item strong {
  display: block;
  margin-top: 4px;
  color: #3e2c14;
}

.effect-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.score-breakdown {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.ok-text {
  color: #1e9e65;
}

.warn-text {
  color: #3d7bd1;
}

.dot {
  margin: 0 6px;
  color: #baa17a;
}

/* ===== 真灵选项 (创建/编辑小队) ===== */
.aura-option {
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #dce7f7;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.aura-option.selected {
  border-color: #b88746;
  background: #fff9ef;
}

.aura-option.disabled {
  opacity: 0.6;
}

.aura-option-body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aura-option-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  background: #f0e6d0;
}

.aura-option-name {
  font-weight: 600;
  color: #3d2c12;
}

.aura-option-tags {
  margin: 6px 0;
}

.aura-option-stats {
  font-size: 12px;
  color: #6d84a3;
}

/* ===== 奖励弹窗 ===== */
.reward-message,
.reward-rest {
  margin-bottom: 14px;
  color: #604c31;
}

.reward-exp-block {
  margin-bottom: 16px;
}

.block-title {
  margin-bottom: 10px;
  font-weight: 600;
  color: #4a3419;
}

.reward-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
}

.reward-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reward-card {
  padding: 14px;
  border: 1px solid #dce7f7;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.exp-card {
  background: #fff8ee;
}

.reward-name {
  font-weight: 700;
  color: #1f3f67;
}

.reward-meta {
  font-size: 12px;
  color: #6d84a3;
}

.reward-desc {
  margin: 10px 0 0;
  line-height: 1.65;
  color: #49627f;
}

.reward-count {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 700;
  color: #b97719;
}

/* ===== 响应式 ===== */
@media (max-width: 991px) {
  .explore-page {
    padding: 12px;
  }


  .main-layout {
    flex-direction: column;
  }

  .side-nav {
    width: 100%;
    flex-direction: row;
    position: static;
    overflow-x: auto;
  }

  .nav-item {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .map-grid {
    grid-template-columns: 1fr;
  }

  .guide-grid,
  .guide-grid-2 {
    grid-template-columns: 1fr;
  }

  .recommend-grid,
  .material-guide-grid {
    grid-template-columns: 1fr;
  }

  .expand-grid,
  .score-breakdown {
    grid-template-columns: 1fr;
  }


  .launch-summary-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .summary-divider {
    display: none;
  }
}

@media (max-width: 768px) {
  .explore-page,
  .main-layout,
  .content-area,
  .tab-content,
  .panel-card {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
  }

  .explore-page {
    padding: 4px !important;
    overflow-x: hidden !important;
  }

  .main-layout {
    display: block !important;
  }

  .side-nav {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 5px;
    width: 100% !important;
    margin-bottom: 8px;
    overflow: visible !important;
  }

  .nav-item {
    min-width: 0 !important;
    width: 100%;
    padding: 8px 3px;
    gap: 3px;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    white-space: normal !important;
    font-size: 12px;
  }

  .nav-item .ivu-tag {
    max-width: 100%;
    margin: 1px 0 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .panel-card >>> .ivu-card-head,
  .panel-card >>> .ivu-card-body {
    padding: 10px !important;
  }

  .panel-title,
  .map-block-head,
  .map-block-foot,
  .record-head,
  .record-foot,
  .team-summary {
    min-width: 0;
    flex-wrap: wrap;
  }

  .panel-subtitle {
    font-size: 11px;
  }

  .map-grid,
  .guide-grid,
  .guide-grid-2,
  .recommend-grid,
  .material-guide-grid,
  .expand-grid,
  .score-breakdown {
    grid-template-columns: 1fr !important;
  }

  .map-block-desc,
  .record-desc,
  .material-guide-main strong {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-break: break-word;
  }

  .strategy-group {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .strategy-card {
    min-width: 0 !important;
    width: 100%;
    padding: 8px;
  }

  .hours-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .hours-list .ivu-btn {
    width: 100%;
    min-width: 0;
    margin: 0;
  }

  .launch-summary-bar,
  .team-selector-row,
  .launch-confirm-layout {
    width: 100%;
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .team-selector-row >>> .ivu-select {
    width: 100% !important;
  }

  .team-selector-row .ivu-btn {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
  }

  .slot-row {
    grid-template-columns: 1fr !important;
  }

  .slot-block {
    text-align: left;
  }

  .slot-block-body {
    align-items: flex-start;
  }

  .launch-check {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
    padding: 10px;
  }

  .launch-alerts,
  .launch-btn {
    width: 100%;
    min-width: 0;
  }

  .record-tabs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .record-tab {
    min-width: 0;
    padding: 7px 3px;
    justify-content: center;
    white-space: normal;
    text-align: center;
    font-size: 11px;
  }

  .record-scroll-area {
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }

  .record-actions {
    flex-wrap: wrap;
  }
}

.cave-xian-glyph,
.farm-travel-glyph {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  min-width: 1.2em;
  color: currentColor;
  font-family: "KaiTi", "STKaiti", "FZKai-Z03", serif;
  font-weight: 900;
  line-height: 1;
}

.guide-glyph { color: #c18b34; font-size: 17px; }
.map-check-glyph { color: #2d8cf0; font-size: 20px; }
.summary-arrow { color: #6c8fa2; font-size: 12px; }
.farm-travel-glyph { margin-right: 3px; color: #5d8297; }
.farm-travel-active-list { display: grid; gap: 5px; }
.farm-travel-active-list-head { display: flex; justify-content: space-between; color: #806b4d; font-size: 10px; }
.farm-travel-active-list-head span { color: #9b6f17; }
.farm-travel-active-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 7px 8px; color: #654d31; background: #fffdf2; border: 1px solid #d8c18c; border-radius: 3px; text-align: left; cursor: pointer; }
.farm-travel-active-row.active { background: #eef5eb; border-color: #6b9672; box-shadow: inset 3px 0 #5f8b67; }
.farm-travel-active-row span { display: grid; min-width: 0; gap: 2px; }
.farm-travel-active-row b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; }
.farm-travel-active-row small { color: #8a7454; font-size: 9px; }
.farm-travel-active-row em { flex: 0 0 auto; color: #5f8b67; font-size: 10px; font-style: normal; }
.farm-travel-team-marker { position: absolute; z-index: 4; display: flex; flex-direction: column; align-items: center; gap: 1px; min-width: 54px; padding: 0; color: #fff6ca; background: transparent; border: 0; text-shadow: 0 1px 2px #172822, 0 0 4px #172822; cursor: pointer; transform: translate(-50%, -50%); }
.farm-travel-team-marker i { display: grid; place-items: center; width: 23px; height: 23px; color: #fff; background: #a86f32; border: 2px solid #ffe49d; border-radius: 50%; box-shadow: 0 0 0 1px rgba(74,47,20,.7), 0 0 10px rgba(255,219,120,.86); font-style: normal; }
.farm-travel-team-marker.completed i { background: #b6534b; }
.farm-travel-team-marker.active i { width: 29px; height: 29px; background: #4b8b5b; box-shadow: 0 0 0 2px #fff0ab, 0 0 15px rgba(177,245,156,1); }
.farm-travel-team-marker span { max-width: 90px; overflow: hidden; font-size: 9px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.farm-travel-team-marker small { color: #ffe29b; font-size: 8px; }
.farm-travel-map { touch-action: pan-y; }

@media (max-width: 720px) {
  .farm-travel-team-marker span { max-width: 72px; }
  .farm-travel-active-row { padding: 8px; }
}
.farm-travel-mode{color:#315a7c}.farm-travel-toolbar{background:#fff;border-color:#b9cde0;box-shadow:0 4px 14px rgba(66,101,129,.08)}.farm-travel-kicker{color:#5f8eb6}.farm-travel-toolbar h3{color:#315a7c}.farm-travel-toolbar p,.farm-travel-toolbar-actions{color:#6f8da6}.farm-travel-toolbar-actions .ivu-btn{color:#426581;background:#f7fbff;border-color:#b9cde0}.farm-travel-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-bottom:10px}.farm-travel-summary>div{display:grid;gap:3px;min-width:0;padding:8px 10px;background:#f7fbff;border:1px solid #c9dced;border-radius:4px}.farm-travel-summary small{color:#7391aa;font-size:9px}.farm-travel-summary strong{overflow:hidden;color:#315a7c;font-size:12px;text-overflow:ellipsis;white-space:nowrap}.farm-travel-summary span{overflow:hidden;color:#6f8da6;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.farm-travel-summary .summary-ready{background:#eef8f3;border-color:#a8d1bd}.farm-travel-summary .summary-ready strong{color:#3f8061}.farm-travel-summary .summary-warning{background:#fff9ef;border-color:#e5cc9d}.farm-travel-summary .summary-warning strong{color:#a27632}.farm-travel-map{border-color:#7fa9ca;box-shadow:inset 0 0 0 1px rgba(255,255,255,.7),0 5px 18px rgba(66,101,129,.12)}.farm-travel-map-shade{background:rgba(238,247,253,.08)}.farm-travel-roads polyline{stroke:#b9d7ec;filter:drop-shadow(0 0 .25px #426581)}.farm-travel-detail{color:#315a7c;background:#fff;border-color:#b9cde0;box-shadow:0 4px 14px rgba(66,101,129,.08)}.farm-travel-detail-head{border-color:#d7e6f1}.farm-travel-detail-head span{color:#5f8eb6}.farm-travel-detail-head h4{color:#315a7c}.farm-travel-detail-head strong{color:#3978b7}.farm-travel-description{color:#6f8da6}.farm-travel-detail-stats span{background:#f7fbff;border-color:#c9dced}.farm-travel-detail-stats small{color:#7391aa}.farm-travel-detail-stats b{color:#3978b7}.farm-travel-detail-fit{display:grid;grid-template-columns:auto auto;gap:3px 8px;padding:7px 8px;background:#eef6fd;border-left:3px solid #5b96c4}.farm-travel-detail-fit span{color:#6f8da6;font-size:9px}.farm-travel-detail-fit strong{font-size:11px;text-align:right}.farm-travel-detail-fit small{grid-column:1/-1;color:#7391aa;font-size:9px}.fit-ok{color:#3f8061}.fit-warning{color:#a27632}.farm-travel-config>label{color:#5f8eb6}.farm-travel-choice-list button,.farm-travel-hours button{color:#426581;background:#f7fbff;border-color:#b9cde0}.farm-travel-choice-list button.active,.farm-travel-hours button.active{color:#fff;background:#3978b7;border-color:#2f669d}.farm-travel-choice-list small{color:#7391aa}.farm-travel-choice-list button.active small{color:#e8f4fc}.farm-travel-recommend{background:#eef8f3;border-left-color:#5d9b7b}.farm-travel-recommend strong{color:#4b8668}.farm-travel-recommend span,.farm-travel-recommend small{color:#6a897b}.farm-travel-match{background:#fff9ef;border-color:#e5cc9d}.farm-travel-match span{color:#a27632}.farm-travel-match small{color:#806f55}.farm-travel-start-button,.farm-travel-detail .ivu-btn-primary{color:#fff;background:#3978b7;border-color:#2f669d}.farm-travel-running{background:#eef8f3;border-left-color:#5d9b7b}.farm-travel-running span,.farm-travel-running small{color:#6f8da6}.farm-travel-running strong{color:#3f8061}.farm-travel-record-strip,.farm-travel-team-bar{background:#fff;border-color:#b9cde0}.farm-travel-record-strip>strong,.farm-travel-team-title strong{color:#5f8eb6}.farm-travel-record-strip>button{color:#426581;background:#f7fbff;border-color:#b9cde0}.farm-travel-record-strip button small,.farm-travel-team-title span{color:#7391aa}.farm-travel-finish-time{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 8px;color:#6f8da6;background:#f7fbff;border:1px solid #c9dced;border-radius:3px;font-size:9px}.farm-travel-finish-time strong{color:#315a7c;font-size:10px}
@media (max-width:720px){.farm-travel-summary{grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.farm-travel-summary>div{padding:7px}.farm-travel-summary strong{font-size:11px}.farm-travel-summary span{font-size:8px}.farm-travel-toolbar-actions{width:100%;justify-content:space-between}.farm-travel-finish-time{min-height:29px}}
</style>

<style>
/* Final white-and-blue pass for the travel map and its detail panel. */
.aura-travel-modal .farm-travel-mode,
.aura-travel-modal .farm-travel-detail,
.aura-travel-modal .farm-travel-toolbar,
.aura-travel-modal .farm-travel-team-bar,
.aura-travel-modal .farm-travel-record-strip {
  color: #315a7c !important;
  background: #ffffff !important;
  border-color: #b9cde0 !important;
}
.aura-travel-modal .farm-travel-toolbar h3,
.aura-travel-modal .farm-travel-detail-head h4,
.aura-travel-modal .farm-travel-detail-head strong,
.aura-travel-modal .farm-travel-detail-stats b,
.aura-travel-modal .farm-travel-team-title strong,
.aura-travel-modal .farm-travel-record-strip > strong {
  color: #315a7c !important;
}
.aura-travel-modal .farm-travel-toolbar p,
.aura-travel-modal .farm-travel-toolbar-actions,
.aura-travel-modal .farm-travel-detail-head span,
.aura-travel-modal .farm-travel-description,
.aura-travel-modal .farm-travel-detail-stats small,
.aura-travel-modal .farm-travel-detail-fit span,
.aura-travel-modal .farm-travel-detail-fit small,
.aura-travel-modal .farm-travel-config > label,
.aura-travel-modal .farm-travel-record-strip button small {
  color: #6f8da6 !important;
}
.aura-travel-modal .farm-travel-detail-stats span,
.aura-travel-modal .farm-travel-detail-fit,
.aura-travel-modal .farm-travel-finish-time {
  color: #426581 !important;
  background: #f7fbff !important;
  border-color: #c9dced !important;
}
.aura-travel-modal .farm-travel-choice-list button,
.aura-travel-modal .farm-travel-hours button,
.aura-travel-modal .farm-travel-record-strip > button,
.aura-travel-modal .farm-travel-toolbar-actions .ivu-btn {
  color: #426581 !important;
  background: #f7fbff !important;
  border-color: #b9cde0 !important;
}
.aura-travel-modal .farm-travel-choice-list button:hover,
.aura-travel-modal .farm-travel-hours button:hover,
.aura-travel-modal .farm-travel-record-strip > button:hover,
.aura-travel-modal .farm-travel-toolbar-actions .ivu-btn:hover,
.aura-travel-modal .farm-travel-choice-list button.active,
.aura-travel-modal .farm-travel-hours button.active {
  color: #ffffff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
}
.aura-travel-modal .farm-travel-choice-list button.active small {
  color: #e8f4fc !important;
}
.aura-travel-modal .farm-travel-recommend,
.aura-travel-modal .farm-travel-running {
  background: #eef8f3 !important;
  border-left-color: #5d9b7b !important;
}
.aura-travel-modal .farm-travel-recommend strong,
.aura-travel-modal .farm-travel-running strong,
.aura-travel-modal .fit-ok {
  color: #3f8061 !important;
}
.aura-travel-modal .farm-travel-recommend span,
.aura-travel-modal .farm-travel-recommend small,
.aura-travel-modal .farm-travel-running span,
.aura-travel-modal .farm-travel-running small {
  color: #6a897b !important;
}
.aura-travel-modal .farm-travel-match {
  background: #eef6fd !important;
  border-color: #c9dced !important;
}
.aura-travel-modal .farm-travel-match span,
.aura-travel-modal .fit-warning {
  color: #5f96bf !important;
}
.aura-travel-modal .farm-travel-detail .ivu-btn,
.aura-travel-modal .farm-travel-detail .ivu-btn-primary,
.aura-travel-modal .farm-travel-start-button {
  color: #ffffff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
}
.aura-travel-modal .farm-travel-detail .ivu-btn:hover,
.aura-travel-modal .farm-travel-start-button:hover {
  background: #2f669d !important;
  border-color: #285a8d !important;
}
</style>

<style>
.team-modal.team-modal .aura-option{min-height:128px!important;margin-bottom:8px!important;padding:8px!important;background:#fff!important;border:1px solid #c9dced!important;border-radius:5px!important}.team-modal.team-modal .aura-option.selected{background:#eef6fd!important;border-color:#3978b7!important;box-shadow:inset 3px 0 #3978b7!important}.team-modal.team-modal .aura-option-body{display:grid!important;grid-template-columns:42px minmax(0,1fr)!important;align-items:start!important;gap:7px!important}.team-modal.team-modal .aura-option-avatar{width:42px!important;height:42px!important;border-radius:6px!important}.team-modal.team-modal .aura-option-meta{min-width:0!important;display:grid!important;gap:4px!important}.team-modal.team-modal .aura-option-name{display:flex!important;align-items:center!important;flex-wrap:wrap!important;gap:3px!important;min-width:0!important;color:#315a7c!important;font-size:11px!important;line-height:1.25!important}.team-modal.team-modal .aura-option-name>.ivu-tag{margin:0!important;padding:0 3px!important;font-size:8px!important;line-height:16px!important}.team-modal.team-modal .aura-option-tags{display:flex!important;gap:3px!important;margin:0!important;min-width:0!important}.team-modal.team-modal .aura-option-tags>.ivu-tag{margin:0!important;padding:0 4px!important;font-size:8px!important;line-height:16px!important}.team-modal.team-modal .aura-option-stats{padding-top:3px!important;color:#6f8da6!important;border-top:1px solid #e2edf5!important;font-size:9px!important;line-height:1.3!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important}.aura-travel-modal .farm-travel-layout{grid-template-columns:minmax(0,1.62fr) minmax(280px,.78fr)!important;align-items:stretch;min-width:0;overflow:hidden}
@media (max-width:720px){.aura-travel-modal .farm-travel-map-shortcuts{position:absolute!important;left:8px!important;right:8px!important;top:auto!important;bottom:8px!important;margin:0!important;max-height:96px!important}.aura-travel-modal .farm-travel-map-shortcuts-list{grid-template-columns:repeat(3,minmax(0,1fr))!important;max-height:64px!important}}
.aura-travel-modal .farm-travel-layout>*{min-width:0;max-width:100%}
.aura-travel-modal .farm-travel-map{width:100%;min-width:0;overflow:hidden}
.aura-travel-modal .farm-travel-detail{width:100%;min-width:0;overflow-y:auto;overflow-x:hidden}
.aura-travel-modal .farm-travel-map-shortcuts{position:absolute;z-index:6;left:10px;right:10px;top:auto;bottom:10px;display:grid;gap:6px;padding:8px;background:rgba(32,69,96,.58);border:1px solid rgba(215,236,249,.62);border-radius:5px;box-shadow:0 4px 16px rgba(22,52,73,.2);backdrop-filter:blur(4px)}
.aura-travel-modal .farm-travel-map-shortcuts-head{display:flex;align-items:baseline;gap:8px;color:#f2f9fd}.aura-travel-modal .farm-travel-map-shortcuts-head strong{font-size:11px}.aura-travel-modal .farm-travel-map-shortcuts-head span{color:#c8dfed;font-size:9px}
.aura-travel-modal .farm-travel-map-shortcuts-list{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:5px;max-height:100px;overflow:auto}
.aura-travel-modal .farm-travel-shortcut{display:grid;gap:2px;min-width:0;padding:5px 6px;color:#f3f9fc;background:rgba(239,248,253,.18);border:1px solid rgba(214,235,248,.42);border-radius:3px;text-align:left;cursor:pointer}
.aura-travel-modal .farm-travel-shortcut:hover,.aura-travel-modal .farm-travel-shortcut.active{background:rgba(91,157,201,.72);border-color:#d6edf9}.aura-travel-modal .farm-travel-shortcut.disabled{opacity:.5;cursor:not-allowed}.aura-travel-modal .farm-travel-shortcut small{overflow:hidden;color:#c8dfed;font-size:8px;text-overflow:ellipsis;white-space:nowrap}.aura-travel-modal .farm-travel-shortcut--material{background:rgba(194,222,239,.14)}
.aura-travel-modal .farm-travel-finish-time{margin-top:1px}
@media (max-width:720px){.aura-travel-modal .farm-travel-layout{grid-template-columns:1fr!important;overflow:visible}.aura-travel-modal .farm-travel-map{min-height:360px!important}.aura-travel-modal .farm-travel-map-shortcuts{position:relative;left:auto;right:auto;top:auto;margin:8px;max-height:118px}.aura-travel-modal .farm-travel-map-shortcuts-list{grid-template-columns:repeat(2,minmax(0,1fr));max-height:76px}.aura-travel-modal .farm-travel-map-shortcuts-head{display:grid;gap:2px}.aura-travel-modal .farm-travel-map-shortcuts-head span{font-size:8px}.aura-travel-modal .farm-travel-detail{overflow:visible}}
</style>

<style>
.aura-travel-modal .farm-travel-map{min-height:520px!important;aspect-ratio:1.72!important;background-color:#dbeaf4!important;background-image:url('~@/assets/spirit-world-ink-map.jpg')!important;border-color:#7fa9ca!important}
.aura-travel-modal .farm-travel-map-image{position:absolute;inset:0;width:100%;height:100%;z-index:0;display:block;object-fit:cover;object-position:center;opacity:1;pointer-events:none}
.aura-travel-modal .farm-travel-map-shade{z-index:1;background:rgba(238,247,253,.08)!important}
.aura-travel-modal .farm-travel-roads,.aura-travel-modal .farm-travel-origin,.aura-travel-modal .farm-travel-marker,.aura-travel-modal .farm-travel-team-marker,.aura-travel-modal .farm-travel-map-empty{z-index:2}
.aura-travel-modal .farm-travel-layout{grid-template-columns:minmax(0,1.75fr) minmax(300px,.72fr)!important}
.aura-travel-modal .farm-travel-detail{min-height:520px!important}
.aura-travel-modal .farm-travel-summary{grid-template-columns:repeat(4,minmax(0,1fr))}
@media (max-width:720px){.aura-travel-modal .farm-travel-map{min-height:360px!important;aspect-ratio:1.28!important}.aura-travel-modal .farm-travel-layout{grid-template-columns:1fr!important}.aura-travel-modal .farm-travel-detail{min-height:0!important}.aura-travel-modal .farm-travel-summary{grid-template-columns:repeat(2,minmax(0,1fr))}}
</style>

<style>
.farm-travel-origin { position: absolute; z-index: 3; left: 36%; top: 43%; display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 0; color: #fff2ac; background: transparent; border: 0; text-shadow: 0 1px 2px #172822, 0 0 4px #172822; cursor: pointer; transform: translate(-50%, -50%); }
.farm-travel-origin i { display: grid; place-items: center; width: 17px; height: 17px; color: #fff; background: #6b9b5d; border: 1px solid #fff4bf; border-radius: 3px; box-shadow: 0 0 0 1px rgba(38, 87, 49, .76), 0 0 8px rgba(177, 245, 156, .9); font: 900 9px/1 "KaiTi", serif; font-style: normal; }
.farm-travel-origin.active i { box-shadow: 0 0 0 2px #ffe29b, 0 0 12px rgba(177, 245, 156, 1); }
.farm-travel-origin span { font-size: 10px; font-weight: 800; white-space: nowrap; }
.farm-travel-map { aspect-ratio: 1.5; min-height: 0; }
.farm-travel-bottom { display: grid; gap: 8px; margin-top: 10px; }
.farm-travel-team-bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 10px; background: #fff9e9; border: 1px solid #d8c59a; border-radius: 4px; }
.farm-travel-team-title { display: grid; gap: 2px; min-width: 0; }
.farm-travel-team-title strong { color: #654d31; font-size: 12px; }
.farm-travel-team-title span { color: #8a7454; font-size: 9px; }
.farm-travel-team-actions { display: flex; align-items: center; justify-content: flex-end; gap: 5px; min-width: 0; }
.farm-travel-team-actions .ivu-select { width: 240px; }
.farm-travel-marker.map-type-2 i { width: 14px; height: 13px; border: 0; border-radius: 0; background: #b54d3c; clip-path: polygon(50% 0, 100% 100%, 0 100%); }
.farm-travel-marker.map-type-3 i { border-radius: 2px; background: #8267b6; transform: rotate(45deg); }
.farm-travel-marker i { width: 13px; height: 13px; border-width: 1.5px; }
.farm-travel-marker.active i { width: 17px; height: 17px; box-shadow: 0 0 0 1px rgba(38, 87, 49, .76), 0 0 8px rgba(177, 245, 156, 1); }

.farm-travel-mode{color:#31463d}.farm-travel-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px;padding:10px 12px;background:#fff9e9;border:1px solid #d8c59a;border-radius:4px}.farm-travel-kicker{color:#a0865d;font-size:10px}.farm-travel-toolbar h3{margin:3px 0;color:#654a2e;font:900 19px/1.2 "KaiTi","STKaiti",serif}.farm-travel-toolbar p{margin:0;color:#947d5d;font-size:10px}.farm-travel-toolbar-actions{display:flex;align-items:center;gap:8px;color:#8a7454;font-size:10px;white-space:nowrap}.farm-travel-layout{display:grid;grid-template-columns:minmax(0,1.55fr) minmax(270px,.85fr);gap:10px}.farm-travel-map{position:relative;min-height:420px;overflow:hidden;border:2px solid #8e713f;border-radius:5px;background-position:center;background-size:cover;box-shadow:inset 0 0 0 1px rgba(255,246,205,.48)}.farm-travel-map-shade{position:absolute;inset:0;background:rgba(19,31,25,.12);pointer-events:none}.farm-travel-roads{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}.farm-travel-roads polyline{fill:none;stroke:#f6df91;stroke-width:.35;stroke-dasharray:1.2 1;opacity:.78;filter:drop-shadow(0 0 .25px #513b1e)}.farm-travel-marker{position:absolute;z-index:2;display:flex;flex-direction:column;align-items:center;gap:2px;min-width:50px;padding:0;color:#fff2ac;background:transparent;border:0;text-shadow:0 1px 2px #172822,0 0 4px #172822;cursor:pointer;transform:translate(-50%,-50%)}.farm-travel-marker i{display:grid;place-items:center;width:19px;height:19px;border:2px solid #fff4bf;border-radius:50%;background:#4b91b5;box-shadow:0 0 0 1px rgba(74,47,20,.66),0 0 8px rgba(255,226,121,.9);font-style:normal}.farm-travel-marker i b{font-size:9px}.farm-travel-marker span{max-width:86px;overflow:hidden;font-size:10px;font-weight:800;text-overflow:ellipsis;white-space:nowrap}.farm-travel-marker small{color:#d4eac6;font-size:8px}.farm-travel-marker.active i{width:25px;height:25px;background:#4d9165;box-shadow:0 0 0 2px #ffe29b,0 0 14px rgba(177,245,156,1)}.farm-travel-marker.running i{background:#c58b3e}.farm-travel-marker.running small{color:#ffe29b}.farm-travel-map-empty{position:absolute;inset:0;display:grid;place-items:center;color:#fff2c4;font-size:12px;text-shadow:0 1px 3px #172822}.farm-travel-detail{display:flex;min-height:420px;flex-direction:column;gap:8px;padding:12px;color:#4d3519;background:#fff8e7;border:1px solid #c9ad71;border-radius:5px}.farm-travel-detail-head{display:flex;align-items:flex-end;justify-content:space-between;gap:8px;padding-bottom:8px;border-bottom:1px solid #e2d2aa}.farm-travel-detail-head span{color:#8a6936;font-size:10px;font-weight:800}.farm-travel-detail-head h4{margin:3px 0 0;font-size:19px}.farm-travel-detail-head strong{color:#9b6f17;font-size:11px}.farm-travel-description{margin:0;color:#6c5534;font-size:11px;line-height:1.55}.farm-travel-detail-stats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.farm-travel-detail-stats span{display:grid;gap:3px;padding:7px;background:#f4ead0;border:1px solid #e2d2aa}.farm-travel-detail-stats small{color:#8a7454;font-size:9px}.farm-travel-detail-stats b{color:#5d785b;font-size:11px}.farm-travel-config{display:grid;gap:6px;margin-top:auto}.farm-travel-config>label{color:#806b4d;font-size:10px;font-weight:800}.farm-travel-choice-list{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:4px}.farm-travel-choice-list button,.farm-travel-hours button{min-width:0;padding:6px 4px;color:#6c5534;background:#fffdf2;border:1px solid #d8c18c;border-radius:3px;text-align:center;cursor:pointer}.farm-travel-choice-list button.active,.farm-travel-hours button.active{color:#fff8db;background:#5f8b67;border-color:#477453}.farm-travel-choice-list strong,.farm-travel-choice-list small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.farm-travel-choice-list strong{font-size:10px}.farm-travel-choice-list small{margin-top:2px;color:#806b4d;font-size:8px}.farm-travel-choice-list button.active small{color:#e5f2dc}.farm-travel-hours{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:4px}.farm-travel-hours button{font-size:10px}.farm-travel-recommend{display:grid;grid-template-columns:auto 1fr;gap:4px 8px;padding:7px;background:#eef5eb;border-left:3px solid #6b9672}.farm-travel-recommend strong{color:#477453;font-size:10px}.farm-travel-recommend span,.farm-travel-recommend small{grid-column:2;color:#5e755f;font-size:9px;line-height:1.35}.farm-travel-recommend span:before{content:'· ';color:#6b9672}.farm-travel-match{display:grid;gap:2px;padding:6px 8px;background:#f4ead0;border:1px solid #e2d2aa}.farm-travel-match span{color:#9b6f17;font-size:11px;font-weight:800}.farm-travel-match small{color:#806b4d;font-size:9px;line-height:1.35}.farm-travel-running{display:grid;gap:7px;margin-top:auto;padding:9px;background:#eef5eb;border-left:4px solid #6b9672}.farm-travel-running>div{display:flex;justify-content:space-between;gap:8px}.farm-travel-running span,.farm-travel-running small{color:#6c7f6e;font-size:10px}.farm-travel-running strong{color:#3f6549;font-size:12px}.farm-travel-running button{width:100%;margin-top:2px}.farm-travel-record-strip{display:flex;align-items:center;gap:6px;margin-top:10px;padding:8px 10px;overflow-x:auto;background:#fff9e9;border:1px solid #d8c59a;border-radius:4px}.farm-travel-record-strip>strong{flex:0 0 auto;color:#806b4d;font-size:10px}.farm-travel-record-strip>button{display:grid;gap:2px;min-width:116px;padding:5px 7px;color:#654d31;background:#fffdf2;border:1px solid #d8c18c;border-radius:3px;text-align:left;cursor:pointer}.farm-travel-record-strip button span{overflow:hidden;font-size:10px;text-overflow:ellipsis;white-space:nowrap}.farm-travel-record-strip button small{overflow:hidden;color:#8a7454;font-size:9px;text-overflow:ellipsis;white-space:nowrap}.farm-travel-record-strip>span{color:#a18a68;font-size:10px;white-space:nowrap}
@media(max-width:720px){.farm-travel-toolbar{align-items:flex-start;flex-direction:column}.farm-travel-toolbar-actions{width:100%;justify-content:space-between}.farm-travel-layout{grid-template-columns:1fr}.farm-travel-map{min-height:300px}.farm-travel-detail{min-height:0}.farm-travel-choice-list{grid-template-columns:repeat(3,minmax(0,1fr))}.farm-travel-record-strip{margin-bottom:2px}}
.farm-travel-team-bar { flex-wrap: wrap; }
@media (max-width: 720px) { .farm-travel-team-actions { width: 100%; display: grid; grid-template-columns: 1fr 1fr; } .farm-travel-team-actions .ivu-select { width: 100%; grid-column: 1 / -1; } .farm-travel-team-actions .ivu-btn { width: 100%; min-width: 0; } }
.farm-travel-marker.map-type-2 i{width:14px;height:13px;border:0;border-radius:0;background:#b54d3c;clip-path:polygon(50% 0,100% 100%,0 100%)}.farm-travel-marker.map-type-3 i{border-radius:2px;background:#8267b6;transform:rotate(45deg)}.farm-travel-marker i{width:13px;height:13px;border-width:1.5px}.farm-travel-marker.active i{width:17px;height:17px;box-shadow:0 0 0 1px rgba(38,87,49,.76),0 0 8px rgba(177,245,156,1)}
</style>

<style>
@media (max-width: 768px) {
  body .team-modal .ivu-modal-body {
    overflow-x: hidden !important;
  }

  body .team-modal .ivu-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  body .team-modal .ivu-col {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  body .team-modal .ivu-checkbox-group,
  body .team-modal .ivu-checkbox-wrapper,
  body .team-modal .aura-option,
  body .team-modal .aura-option-body,
  body .team-modal .aura-option-meta {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
  }

  body .team-modal .ivu-checkbox-wrapper {
    display: flex !important;
    margin: 0 !important;
  }

  body .team-modal .aura-option {
    margin-bottom: 8px;
    padding: 9px !important;
    overflow: hidden;
  }

  body .team-modal .aura-option-body {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  body .team-modal .aura-option-avatar {
    width: 48px !important;
    height: 48px !important;
    flex: 0 0 48px;
  }

  body .team-modal .aura-option-name,
  body .team-modal .aura-option-stats {
    white-space: normal !important;
    word-break: break-word;
  }

  body .team-modal .ivu-modal-footer {
    display: flex;
    gap: 6px;
  }

  body .team-modal .ivu-modal-footer .ivu-btn {
    flex: 1 1 0;
    min-width: 0;
    margin: 0 !important;
  }
}
.aura-travel-modal .farm-travel-toolbar-actions{display:flex;align-items:flex-start;gap:8px;min-width:0}.aura-travel-modal .farm-travel-team-quick{display:grid;gap:5px;min-width:0;max-width:min(760px,70vw)}.aura-travel-modal .farm-travel-team-quick-head{display:flex;align-items:center;gap:6px;min-width:0;color:#315a7c}.aura-travel-modal .farm-travel-team-quick-head>span{font-weight:800;font-size:11px;white-space:nowrap}.aura-travel-modal .farm-travel-team-quick-head>small{color:#6f8da6;font-size:9px;white-space:nowrap}.aura-travel-modal .farm-travel-team-quick-head .ivu-btn{height:25px;min-height:25px;padding:2px 7px;font-size:9px}.aura-travel-modal .farm-travel-team-quick-list{display:flex;gap:4px;min-width:0;overflow-x:auto;padding-bottom:1px}.aura-travel-modal .farm-travel-team-quick-list button{display:grid;gap:1px;flex:0 0 auto;min-width:92px;padding:4px 6px;color:#426581;background:#f7fbff;border:1px solid #b9cde0;border-radius:3px;text-align:left;cursor:pointer}.aura-travel-modal .farm-travel-team-quick-list button.active{color:#fff;background:#3978b7;border-color:#2f669d}.aura-travel-modal .farm-travel-team-quick-list button small{color:#7391aa;font-size:8px}.aura-travel-modal .farm-travel-team-quick-list button.active small{color:#e8f4fc}.aura-travel-modal .farm-travel-team-quick-list>span{align-self:center;color:#8aa1b3;font-size:9px;white-space:nowrap}.aura-travel-modal .farm-travel-map-shortcuts{padding:6px;gap:4px}.aura-travel-modal .farm-travel-map-shortcuts-head strong{font-size:10px}.aura-travel-modal .farm-travel-map-shortcuts-head span{font-size:8px}.aura-travel-modal .farm-travel-map-shortcuts-list{grid-template-columns:repeat(6,minmax(0,1fr));gap:4px;max-height:78px}.aura-travel-modal .farm-travel-shortcut{padding:4px 5px;font-size:9px}.aura-travel-modal .farm-travel-shortcut small{font-size:7px}.team-modal .ivu-modal-content{overflow:hidden;background:#f5f9fc;border:1px solid #b9cde0;border-radius:6px}.team-modal .ivu-modal-header{background:#fff;border-bottom:1px solid #c9dced}.team-modal .ivu-modal-header-inner{color:#315a7c;font-weight:800}.team-modal .ivu-modal-body{padding:14px;background:#f5f9fc}.team-modal .ivu-form-item-label{color:#426581;font-weight:700}.team-modal .ivu-input{color:#315a7c;background:#fff;border-color:#b9cde0}.team-modal .ivu-input:focus{border-color:#5f96bf;box-shadow:0 0 0 2px rgba(95,150,191,.14)}.team-modal .ivu-alert-info{color:#426581;background:#eef6fd;border-color:#c9dced}.team-modal .aura-option{margin-bottom:8px;padding:9px;background:#fff;border:1px solid #c9dced;border-radius:5px;transition:border-color .16s,background .16s,box-shadow .16s}.team-modal .aura-option:hover{background:#f7fbff;border-color:#7fa9ca}.team-modal .aura-option.selected{background:#eef6fd;border-color:#3978b7;box-shadow:inset 3px 0 #3978b7}.team-modal .aura-option.disabled{background:#edf2f6}.team-modal .aura-option-avatar{background:#e6f1f8;border:1px solid #b9cde0;border-radius:6px}.team-modal .aura-option-name{color:#315a7c}.team-modal .aura-option-stats{color:#6f8da6}.team-modal .ivu-modal-footer{background:#fff;border-top:1px solid #c9dced}.team-modal .ivu-modal-footer .ivu-btn{border-color:#b9cde0}.team-modal .ivu-modal-footer .ivu-btn-primary{color:#fff;background:#3978b7;border-color:#2f669d}.team-modal .ivu-modal-footer .ivu-btn-error{color:#fff;background:#b86d70;border-color:#9f565a}@media (max-width:720px){.aura-travel-modal .farm-travel-toolbar-actions{width:100%;display:grid;gap:5px}.aura-travel-modal .farm-travel-team-quick{max-width:none;width:100%}.aura-travel-modal .farm-travel-team-quick-head{flex-wrap:wrap}.aura-travel-modal .farm-travel-team-quick-head>small{margin-right:auto}.aura-travel-modal .farm-travel-team-quick-list button{min-width:84px}.aura-travel-modal .farm-travel-map-shortcuts-list{grid-template-columns:repeat(3,minmax(0,1fr));max-height:86px}}
</style>



// WEBPACK FOOTER //
// src/components/petMap.vue